import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Container,
  Divider,
  Grid,
  Paper,
  Popover,
  Radio,
  Typography,
} from "@mui/material";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { apiPost, fetchData } from "../../../../common/services/ApiClient";
import {
  arraySum,
  chunkArray,
  fileNameFormat,
  reduceObj,
  sortObjValue,
  sumValueArrayObj,
} from "../../../../common/services/Utilities";
import { ApiBaseUrl } from "../../../../config";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import ReportingPeriod from "../../../../containers/ReportingPeriod";
import ResultPagination from "../../../../containers/ResultPagination";
import SearchPills from "../../../../containers/SearchPills";
import SelectedMarketTable from "../../../../containers/SelectedMarketTable";
import {
  KeyNumberValue,
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../../interfaces";
import {
  getStateName,
  selectedStatesAlpha,
} from "../../../../refdata/FilterOptions";
import {
  additionalHeaderColor,
  primaryHeaderColor,
  textHeaderColor,
} from "../../../../refdata/constants";
import ApiDataDownload, { apiDownloadProps } from "../../../ApiDataDownload";
import LoadingIndicator from "../../../LoadingIndicator";
import ModalDialog from "../../../ModalDialog";
import MoreInfoButton from "../../../MoreInfoButton";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";
import generateBranchOfficeDepositsApiUrl from "../../BranchOfficeDeposits/BranchOfficeDepositsUtilities";
import ShowHideButton from "../../../ShowHideButton";

const PREFIX = "ProFormaTable";

const classes = {
  finalizeResetButton: `${PREFIX}-finalizeResetButton`,
  institutionsHeader: `${PREFIX}-institutionsHeader`,
  postMerger: `${PREFIX}-postMerger`,
  tooltipHHI: `${PREFIX}-tooltipHHI`,
  tooltipHHIBody: `${PREFIX}-tooltipHHIBody`,
  bannerStyles: `${PREFIX}-bannerStyles`,
  firstCellDarken: `${PREFIX}-firstCellDarken`,
  firstCellDarkest: `${PREFIX}-firstCellDarkest`,
  secondCellDarken: `${PREFIX}-secondCellDarken`,
  secondCellDarkest: `${PREFIX}-secondCellDarkest`,
  backgroundColorBanner: `${PREFIX}-backgroundColorBanner`,
  groupHover: `${PREFIX}-groupHover`,
  group: `${PREFIX}-group`,
  holdingCompanyTitleStyle: `${PREFIX}-holdingCompanyTitleStyle`,
  bhcCellStyle: `${PREFIX}-bhcCellStyle`,
  bhcRowStyle: `${PREFIX}-bhcRowStyle`,
  finalizeReportPaper: `${PREFIX}-finalizeReportPaper`,
  buttonLink: `${PREFIX}-buttonLink`,
  footer: `${PREFIX}-footer`,
  footerText: `${PREFIX}-footerText`,
  viewResultsButton: `${PREFIX}-viewResultsButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.finalizeResetButton}`]: {
    minWidth: 280,
    minHeight: 40,
    fontSize: 16,
  },

  [`& .${classes.institutionsHeader}`]: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },

  [`& .${classes.postMerger}`]: {
    fontStyle: "italic",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },

  [`& .${classes.tooltipHHI}`]: {
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    paddingBottom: "5px",
  },

  [`& .${classes.tooltipHHIBody}`]: {
    textAlign: "left",
    color: theme.palette.primary.main,
  },

  [`& .${classes.bannerStyles}`]: {
    textAlign: "left",
    color: theme.palette.primary.main,
    padding: 0,
    marginBottom: "1rem",
    fontSize: "16px",
  },

  [`& .${classes.firstCellDarken}`]: {
    minWidth: 60,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },

  [`& .${classes.firstCellDarkest}`]: {
    minWidth: 60,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },

  [`& .${classes.secondCellDarken}`]: {
    minWidth: 60,
    backgroundColor: "rgba(0, 0.1, 0, 0.01)",
  },

  [`& .${classes.secondCellDarkest}`]: {
    minWidth: 60,
    backgroundColor: "rgba(0, 0.1, 0, 0.02)",
  },

  [`& .${classes.backgroundColorBanner}`]: {
    backgroundColor: "rgba(0, 0.1, 0, 0.04)",
    border: "1px solid rgba(0, 0.1, 0, 0.04)",
    paddingLeft: "10px",
  },

  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#C6EDF2",
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.group}`]: {
    textDecoration: "underline",
  },

  [`& .${classes.holdingCompanyTitleStyle}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },

  [`& .${classes.bhcCellStyle}`]: {
    backgroundColor: "#acc7df",
    fontWeight: "bold",
  },

  [`& .${classes.bhcRowStyle}`]: {
    "&:hover td, &:focus-visible td": {
      backgroundColor: "#C6EDF2",
      cursor: "pointer",
    },
  },

  [`& .${classes.finalizeReportPaper}`]: {
    border: "1.2px solid",
    borderColor: theme.palette.primary.main,
    padding: theme.spacing(2),
  },

  [`& .${classes.buttonLink}`]: {
    "fontWeight": "bold",
    "color": "#0000FF",
    "padding": 0,
    "marginTop": 0,
    "fontSize": "1rem",
    "&.MuiButton-root:hover, &.MuiButton-root:focus-visible": {
      backgroundColor: "#fff",
      textDecoration: "underline",
    },
  },

  [`& .${classes.footer}`]: {
    color: theme.palette.primary.main,
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: 400,
    textAlign: "left",
  },

  [`& .${classes.footerText}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 16,
    whiteSpace: "nowrap",
  },

  [`& .${classes.viewResultsButton}`]: {
    "&.MuiButton-root": {
      "width": 115,
      "margin": "0px 8px",
      "float": "left",
      "border": `1px solid ${theme.palette.primary.main}`,
      "&.MuiButton-contained.MuiButton-colorSecondary": {
        backgroundColor: "white",
        border: `1px solid ${theme.palette.primary.light}`,
      },
      "&:hover, &.Mui-focusVisible": {
        color: "#ffffff",
        border: `1px solid ${theme.palette.primary.main}`,

        backgroundColor: theme.palette.primary.main,
      },
      "&.Mui-disabled": {
        backgroundColor: "#dbdbdb",
        borderColor: "#bfcbd8",
        color: "rgba(0, 0, 0, 0.4)",
      },
    },
  },
}));

interface Props {
  appState: Query;
  updatePage: (page: number) => void;
  updateUi: (
    field: string,
    value: KeyValue[] | boolean | string | KeyValue,
  ) => void;
}

const isTestEnvironment = process.env.NODE_ENV === "test";

export function handleCheckboxChange(
  event: React.ChangeEvent<HTMLInputElement>,
  certs: string[],
  partialMergerSelections: KeyValue[],
  setPartialMergerSelections: (selections: KeyValue[]) => void,
  updateUi: (
    field: string,
    value: KeyValue[] | boolean | string | KeyValue,
  ) => void,
  appState: Query,
  addCert: (cert: string[]) => void,
  removeCert: (cert: string[]) => void,
) {
  if (
    partialMergerSelections.some((obj: KeyValue) => certs.includes(obj.cert)) &&
    !isTestEnvironment
  ) {
    const newMergers = reduceObj(
      Object.keys(appState.ui.partialMergerCheckboxes).map((merger) => {
        return {
          key: merger,
          value: appState.ui.partialMergerCheckboxes[merger],
        };
      }),
    );
    certs.forEach((cert) => delete newMergers[cert]);

    updateUi("partialMergerCheckboxes", newMergers);
    setPartialMergerSelections(
      partialMergerSelections.filter(
        (obj: KeyValue) => !certs.includes(obj.cert),
      ),
    );
    updateUi(
      "partialMerger",
      partialMergerSelections.filter(
        (obj: KeyValue) => !certs.includes(obj.cert),
      ),
    );
    addCert(certs);
  } else if (event.target.checked) {
    addCert(certs);
  } else {
    removeCert(certs);
  }
}

export function handleRadioButtonChange(
  event: React.ChangeEvent<HTMLInputElement>,
  cert: string,
  removeCerts: string[],
  removeCert: (cert: string[]) => void,
  setSelectedValue: (val: string) => void,
  setCertAcquiring: (certs: string[]) => void,
  partialMergerSelections: KeyValue[],
  setPartialMergerSelections: (selections: KeyValue[]) => void,
  updateUi: (
    field: string,
    value: KeyValue[] | boolean | string | KeyValue,
  ) => void,
) {
  removeCert(removeCerts);
  setSelectedValue(event.target.value);
  setCertAcquiring(removeCerts);
  updateUi("certAcquiring", removeCerts);
  if (partialMergerSelections.some((obj: KeyValue) => obj.cert === cert)) {
    setPartialMergerSelections(
      partialMergerSelections.filter((obj: KeyValue) => obj.cert !== cert),
    );
  }
}

export default function ProFormaTable({
  appState,
  updatePage,
  updateUi,
}: Readonly<Props>) {
  const isBhc = appState.ui.institutionType === "bhcs";
  const key = isBhc ? "RSSDHCR" : "CERT";

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  //string, number, and boolean
  const [name, setName] = React.useState("");
  const [cert, setCert] = React.useState("");
  const [openModal, setOpenModal] = React.useState("");
  const [selectedValue, setSelectedValue] = React.useState("");
  const [period, setPeriod] = React.useState("");
  const [reportPeriod, setReportPeriod] = React.useState<string>("");
  const [previousResultLimit, setPreviousResultLimit] = React.useState(
    appState.pagination.resultLimit,
  );
  const [recordCount, setRecordCount] = React.useState(0);
  const [hhiNoBHC, setHhiNoBHC] = React.useState(0);
  const [pageJump, setPageJump] = React.useState(false);
  const [scrollToRef, setScrollToRef] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [getInstData, setGetInstData] = React.useState(false);
  const [getAllStatesAggs, setGetAllStatesAggs] = React.useState(false);
  const [getNoBhcData, setGetNoBhcData] = React.useState(false);
  const [getNoBHCInstData, setGetNoBHCInstData] = React.useState(false);
  const [getFinancials, setGetFinancials] = React.useState(false);
  const [onlyFinancials, setOnlyFinancials] = React.useState(false);
  const [getSubData, setGetSubData] = React.useState(false);
  const [getSubFinancials, setGetSubFinancials] = React.useState(false);
  const [withBhc, setWithBhc] = React.useState(isBhc);
  const [rowIsBHC, setRowIsBHC] = React.useState(false);
  const [reportFinalized, setReportFinalized] = React.useState(false);
  const [displayError, setDisplayError] = React.useState(false);
  const [subDataCerts, setSubDataCerts] = React.useState<KeyValue[]>([]);
  const [getSubInstData, setGetSubInstData] = React.useState(false);
  //values used for useEffects and API calls
  const [certArray, setCertArray] = React.useState<number[]>([]);
  const [rssdhcrArray, setRssdhcrArray] = React.useState<number[]>([]);
  const [noBHCDataToMerge, setNoBHCDataToMerge] = React.useState<KeyValue[]>(
    [],
  );
  const [noBHCAcqBHC, setNoBHCAcqBHC] = React.useState(false);
  const [certAcquiring, setCertAcquiring] = React.useState<string[]>([]);
  const [certsToMerge, setCertsToMerge] = React.useState<string[]>([]);
  const [pmMatchingTableData, setPmMatchingTableData] = React.useState<
    KeyValue[]
  >([]);

  const [partialMergerSelections, setPartialMergerSelections] = React.useState<
    KeyValue[]
  >(appState.ui.partialMerger);

  //Table Data Values
  const [totals, setTotals] = React.useState<KeyValue>({});
  const [tableData, setTableData] = React.useState<KeyValue[]>([]);
  const [noBHCData, setNoBHCData] = React.useState<KeyValue[]>([]);
  const [certArrayNoBHC, setCertArrayNoBHC] = React.useState<number[]>([]);
  const [getNoBHCCombinedData, setGetNoBHCCombinedData] = React.useState(false);
  const [selectedMarketData, setSelectedMarketData] = React.useState<
    KeyValue[]
  >([{}]);
  const [partialMergeCount, setPartialMergeCount] = React.useState<KeyValue>(
    {},
  );

  // previous states
  const [previousState, setPreviousState] = React.useState({});
  const [prevTableData, setPrevTableData] = React.useState([{}]);
  const [prevNoBHCData, setPrevNoBHCData] = React.useState<KeyValue[]>([{}]);
  const prevNoBHCDataHHI: number = prevNoBHCData.reduce(
    (sum: number, item: KeyValue) => sum + (item.SQUARED ?? 0),
    0,
  );
  const [prevTotals, setPrevTotals] = React.useState({});

  const headerStyle = (
    bgColor: string,
    align: string,
    textIndent?: string,
    width?: number,
    noWrap?: boolean,
  ): KeyValue => {
    const noWrapStyle = noWrap ? { whiteSpace: "nowrap" } : {};
    return {
      textAlign: align,
      backgroundColor: bgColor,
      color: textHeaderColor,
      textIndent: textIndent,
      fontWeight: "bold",
      width: width,
      ...noWrapStyle,
    };
  };

  const state = React.useMemo(() => {
    return {
      filters: {
        ...appState.filters,
        reportPeriod: [],
        startDate: "",
        endDate: "",
      },
      search: appState.search,
      ui: {
        marketSelection: appState.ui.marketSelection,
        institutionType: appState.ui.institutionType,
      },
    };
  }, [
    appState.filters,
    appState.search,
    appState.ui.institutionType,
    appState.ui.marketSelection,
  ]);

  const reportDate = {
    month: period.substring(4, 6),
    day: period.substring(6, 8),
    year: period.substring(0, 4),
  };
  const reportMonth = reportDate?.month;
  const reportDay = reportDate?.day;
  const reportYear = reportDate?.year;

  const handleScroll = (ref: any) => {
    ref.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const paginationRef = React.useRef(null);

  const paginationDataValues = withBhc ? tableData : noBHCData;

  const paginatedData = paginationDataValues.slice(
    (appState.pagination.pageNumber - 1) * appState.pagination.resultLimit,
    appState.pagination.resultLimit * appState.pagination.pageNumber,
  );
  const addCert = React.useCallback(
    (cert: string[]) => {
      setCertsToMerge([...certsToMerge, ...cert]);
    },
    [certsToMerge],
  );
  const removeCert = React.useCallback(
    (cert: string[]) => {
      setCertsToMerge(
        certsToMerge.filter((mergedCert) => !cert.includes(mergedCert)),
      );
    },
    [certsToMerge],
  );
  const softResetReport = React.useCallback(() => {
    setReportFinalized(false);
    setNoBHCData([]);
    setNoBHCAcqBHC(false);
    setTotals({});
    setSelectedValue("");
    setCertAcquiring([]);
    setPmMatchingTableData([]);
    setPartialMergeCount({});
    !isTestEnvironment && setPartialMergerSelections([]);
    setCertsToMerge([]);
    !isTestEnvironment && updateUi("partialMerger", [{}]);
    updateUi("certAcquiring", []);
    updateUi("partialMergerCheckboxes", {});
  }, [updateUi]);

  const resetReportData = () => {
    setReportFinalized(false);
    setTableData(prevTableData);
    setTotals(prevTotals);
    setNoBHCData(prevNoBHCData);
    setNoBHCAcqBHC(false);
    setNoBHCDataToMerge([]);
    setSelectedValue("");
    setCertAcquiring([]);
    setCertsToMerge([]);
    setPmMatchingTableData([]);
    setPartialMergeCount({});
    setPartialMergerSelections([]);
    updateUi("partialMerger", [{}]);
    updateUi("certAcquiring", []);
    updateUi("partialMergerCheckboxes", {});
  };

  const finalizeReport = () => {
    if (!reportFinalized && certAcquiring[0]) {
      const partialMergerFound = partialMergerSelections.some((obj: KeyValue) =>
        obj.hasOwnProperty("depsum"),
      );
      setPrevTableData(tableData);
      setPrevTotals(totals);
      setReportFinalized(true);
      setPageJump(true);
      let summation = {
        count: 0,
        sum_DEPSUMBR: 0,
        SHARE: 0,
      };
      let acquiringRow: KeyValue[];
      acquiringRow = tableData.filter((item: KeyValue) =>
        certAcquiring.includes(item[key]),
      );
      // this is if the acq row is in the institutions w/o bhc data
      if (
        isBhc &&
        certAcquiring.length > 0 &&
        acquiringRow.length === 0 &&
        noBHCData.length > 0 &&
        noBHCData.some((obj: KeyValue) => certAcquiring.includes(obj?.CERT))
      ) {
        setPrevNoBHCData(noBHCData);
        acquiringRow = noBHCData.filter((item: KeyValue) =>
          certAcquiring.includes(item.CERT),
        );
        setWithBhc(false);
        setNoBHCAcqBHC(true);
        const postMergerData: KeyValue[] = [];
        const partialMerges: KeyValue = {};
        let bhcToMergeRows: KeyValue[] = tableData
          .map((item: KeyValue) => {
            let data = certsToMerge.includes(item[key]) ? item : {};
            let count = 0;
            let deposit = 0;
            let share = 0;
            let partial = 0;
            let postCount = 0;
            let postDeposit = 0;
            let postShare = 0;
            const certList: KeyValue[] = [];
            let postMerger: KeyValue = {};

            if (data.subData) {
              data = {
                ...data,
                subData: data.subData
                  .map((subItem: KeyValue) => {
                    let filteredData;
                    const partialMergerCerts = partialMergerSelections.map(
                      (merger) => merger.cert,
                    );
                    if (
                      certsToMerge.includes(subItem.CERT) &&
                      !partialMergerCerts.includes(subItem.CERT)
                    ) {
                      count += subItem.count;
                      deposit += subItem.sum_DEPSUMBR;
                      share += subItem.SHARE;
                      filteredData = subItem;
                    } else {
                      partial = 1;
                      if (
                        certsToMerge.includes(subItem.CERT) &&
                        partialMergerCerts.includes(subItem.CERT)
                      ) {
                        const partialMergerFound = partialMergerSelections.find(
                          (merger) => merger.cert === subItem.CERT,
                        );
                        if (!certList.includes(subItem.CERT)) {
                          count += partialMergerFound?.count || 0;
                          deposit += partialMergerFound?.depsum || 0;
                          share +=
                            ((partialMergerFound?.depsum || 0) /
                              totals.DEPOSITS) *
                            100;
                        }
                        filteredData = {
                          ...subItem,
                          count: partialMergerFound?.count,
                          sum_DEPSUMBR: partialMergerFound?.depsum,
                          SHARE:
                            ((partialMergerFound?.depsum || 0) /
                              totals.DEPOSITS) *
                            100,
                          PARTIALBANK: 1,
                        };
                      }
                      postMerger = {
                        ...data,
                        subData: data.subData
                          .map((subItem: KeyValue) => {
                            let postMerge;
                            if (
                              certsToMerge.includes(subItem.CERT) &&
                              partialMergerCerts.includes(subItem.CERT)
                            ) {
                              const partialMergerFound =
                                partialMergerSelections.find(
                                  (merger) => merger.cert === subItem.CERT,
                                );

                              if (!certList.includes(subItem.CERT)) {
                                certList.push(subItem.CERT);
                                postCount +=
                                  subItem.count - partialMergerFound?.count;
                                postDeposit +=
                                  subItem.sum_DEPSUMBR -
                                  partialMergerFound?.depsum;
                                postShare +=
                                  ((subItem.sum_DEPSUMBR -
                                    partialMergerFound?.depsum || 0) /
                                    totals.DEPOSITS) *
                                  100;
                              }
                              postMerge = {
                                ...subItem,
                                count:
                                  subItem.count - partialMergerFound?.count,
                                sum_DEPSUMBR:
                                  subItem.sum_DEPSUMBR -
                                  partialMergerFound?.depsum,
                                POST: true,
                              };
                              partialMerges[subItem.CERT] = subItem.count;
                            } else if (!certsToMerge.includes(subItem.CERT)) {
                              if (!certList.includes(subItem.CERT)) {
                                certList.push(subItem.CERT);
                                postCount += subItem.count;
                                postDeposit += subItem.sum_DEPSUMBR;
                                postShare += subItem.SHARE;
                              }
                              postMerge = subItem;
                            }
                            return postMerge;
                          })
                          .filter((data: KeyValue) => data),
                        ASSET: data.ASSET,
                        count: postCount,
                        sum_DEPSUMBR: postDeposit,
                        SHARE: postShare,
                        SQUARED: postShare * postShare,
                        POST: true,
                      };
                    }
                    return filteredData;
                  })
                  .filter((data: KeyValue) => data),

                ASSET: data.ASSET,
                count: count,
                sum_DEPSUMBR: deposit,
                SHARE: share,
                SQUARED: share * share,
                PARTIALBANK: partial,
              };
            }

            if (Object.keys(postMerger).length) {
              partialMerges[data.RSSDHCR] = data.count + postMerger.count;
              postMergerData.push(postMerger);
            }
            return data;
          })
          .filter((data) => Object.keys(data).length > 0);

        //This begins the merging of institutions without a BHC into an acquiring BHC
        let noBHCDataMerge: KeyValue[] = [];
        let noBhcPostMergerRows: KeyValue[] = [];
        noBHCData.forEach((obj: KeyValue) => {
          if (certsToMerge.includes(obj.CERT)) {
            noBHCDataMerge.push(obj);
          }
        });
        setNoBHCDataToMerge(noBHCDataMerge);
        setPrevTotals(totals);
        noBHCData.length && setPrevNoBHCData(noBHCData);

        if (noBHCDataMerge.length) {
          let count = 0;
          let deposit = 0;
          let share = 0;
          let asset = 0;
          noBHCDataMerge = noBHCDataMerge.map((obj: KeyValue) => {
            const noBhcPartialMergerFound = partialMergerSelections.find(
              (merger) => merger.cert === obj.CERT,
            );

            if (noBhcPartialMergerFound) {
              partialMerges[obj.CERT] = obj.count;
              const share =
                ((obj.sum_DEPSUMBR - noBhcPartialMergerFound.depsum) /
                  totals.DEPOSITS) *
                100;
              noBhcPostMergerRows.push({
                ...obj,
                count: obj.count - noBhcPartialMergerFound.count,
                sum_DEPSUMBR: obj.sum_DEPSUMBR - noBhcPartialMergerFound.depsum,
                SHARE: share,
                SQUARED: share * share,
                POST: true,
              });
              count += noBhcPartialMergerFound.count;
              deposit += noBhcPartialMergerFound.depsum;
              asset += obj.ASSET;
              const newShare =
                (noBhcPartialMergerFound.depsum / totals.DEPOSITS) * 100;
              return {
                ...obj,
                count: noBhcPartialMergerFound.count,
                sum_DEPSUMBR: noBhcPartialMergerFound.depsum,
                SHARE: newShare,
                SQUARED: newShare * newShare,
                PARTIALBANK: 1,
                PARTIALMERGECOUNT: obj.count,
                noBHCMerge: true,
              };
            } else {
              count += obj.count;
              deposit += obj.sum_DEPSUMBR;
              asset += obj.ASSET;
              return { ...obj, OLDBANKS: 1, noBHCMerge: true };
            }
          });
          share = (deposit / totals.DEPOSITS) * 100;

          const filteredData = noBHCData.filter(
            (item) =>
              !noBHCDataMerge.some((mergeItem) => mergeItem.CERT === item.CERT),
          );
          setNoBHCData(
            sortObjValue(
              [...filteredData, ...noBhcPostMergerRows],
              "sum_DEPSUMBR",
              true,
            ),
          );
          bhcToMergeRows.push({
            RSSDHCR: "",
            NAMEFULL: "Institutions without BHC",
            STALP: "",
            count: count,
            sum_DEPSUMBR: deposit,
            SHARE: share,
            SQUARED: share * share,
            ASSET: asset,
            subData: sortObjValue(noBHCDataMerge, "sum_DEPSUMBR", true),
            noMergerStatus: true,
          });
        }
        const acqRow = { ...acquiringRow[0], OLDBANKS: 3 };
        bhcToMergeRows.unshift(acqRow);
        setPartialMergeCount(partialMerges);
        const certs = certsToMerge;
        certs.push(...certAcquiring);
        if (partialMergerSelections.length > 0) {
          for (const data of partialMergerSelections) {
            const mergerData = pmMatchingTableData.find(
              (obj: KeyValue) => obj.CERT === data.cert,
            );
            if (mergerData?.count && data.cert) {
              certs.push(data["cert"]);
            }
          }
        }
        const oldInstitutionRows: KeyValue[] = bhcToMergeRows.map((obj) => {
          return { ...obj, OLDBANKS: 1 };
        });
        bhcToMergeRows.forEach((entry: KeyValue) => {
          summation.count += entry.count;
          summation.sum_DEPSUMBR += entry.sum_DEPSUMBR;
          summation.SHARE += entry.SHARE;
        });

        const newNoBHCInstitutionRow: KeyValue = {
          NEWBANK: 1,
          CERT: acquiringRow[0].CERT,
          NAMEFULL: acquiringRow[0].NAMEFULL,
          STALP: acquiringRow[0].STALP,
          BKCLASS: acquiringRow[0].BKCLASS,
          CHARTER: acquiringRow[0].CHARTER,
          ASSET: acquiringRow[0].ASSET,
          old: bhcToMergeRows,
          ...summation,
        };
        newNoBHCInstitutionRow["SQUARED"] =
          newNoBHCInstitutionRow.SHARE * newNoBHCInstitutionRow.SHARE;

        const newTableData: KeyValue[] = tableData.filter(
          (item: KeyValue) => !certs.includes(item[key]),
        );
        let newNoBhcData: KeyValue[] = noBHCData.filter(
          (item: KeyValue) => !certs.includes(item.CERT),
        );
        newNoBhcData = [...newNoBhcData, ...noBhcPostMergerRows];
        newNoBhcData.push(newNoBHCInstitutionRow);
        const allData: KeyValue[] = sortObjValue(
          [...postMergerData, ...newTableData],
          "sum_DEPSUMBR",
          true,
        );
        newNoBhcData = sortObjValue([...newNoBhcData], "sum_DEPSUMBR", true);

        if (oldInstitutionRows[0]?.hasOwnProperty("OLDBANKS")) {
          oldInstitutionRows[0].OLDBANKS = 2;
        }

        newNoBhcData.forEach((data) => {
          if (data.hasOwnProperty("NEWBANK")) {
            data.old = oldInstitutionRows;
          }
        });
        setNoBHCData(newNoBhcData);
        setTableData(allData);
        setWithBhc(false);
      } else {
        const postMergerData: KeyValue[] = [];
        const partialMerges: KeyValue = {};
        const certsToMergeRows: KeyValue[] = tableData
          .map((item: KeyValue) => {
            let data = certsToMerge.includes(item[key]) ? item : {};
            let count = 0;
            let deposit = 0;
            let share = 0;
            let partial = 0;
            let postCount = 0;
            let postDeposit = 0;
            let postShare = 0;
            const certList: KeyValue[] = [];
            let postMerger: KeyValue = {};

            if (data.subData) {
              data = {
                ...data,
                subData: data.subData
                  .map((subItem: KeyValue) => {
                    let filteredData;
                    const partialMergerCerts = partialMergerSelections.map(
                      (merger) => merger.cert,
                    );

                    if (
                      certsToMerge.includes(subItem.CERT) &&
                      !partialMergerCerts.includes(subItem.CERT)
                    ) {
                      count += subItem.count;
                      deposit += subItem.sum_DEPSUMBR;
                      share += subItem.SHARE;
                      filteredData = subItem;
                    } else {
                      partial = 1;
                      if (
                        certsToMerge.includes(subItem.CERT) &&
                        partialMergerCerts.includes(subItem.CERT)
                      ) {
                        const partialMergerFound = partialMergerSelections.find(
                          (merger) => merger.cert === subItem.CERT,
                        );
                        if (!certList.includes(subItem.CERT)) {
                          count += partialMergerFound?.count || 0;
                          deposit += partialMergerFound?.depsum || 0;
                          share +=
                            ((partialMergerFound?.depsum || 0) /
                              totals.DEPOSITS) *
                            100;
                        }

                        filteredData = {
                          ...subItem,
                          count: partialMergerFound?.count,
                          sum_DEPSUMBR: partialMergerFound?.depsum,
                          SHARE:
                            ((partialMergerFound?.depsum || 0) /
                              totals.DEPOSITS) *
                            100,
                          PARTIALBANK: 1,
                        };
                      }
                      postMerger = {
                        ...data,
                        subData: data.subData
                          .map((subItem: KeyValue) => {
                            let postMerge;
                            if (
                              certsToMerge.includes(subItem.CERT) &&
                              partialMergerCerts.includes(subItem.CERT)
                            ) {
                              const partialMergerFound =
                                partialMergerSelections.find(
                                  (merger) => merger.cert === subItem.CERT,
                                );

                              if (!certList.includes(subItem.CERT)) {
                                certList.push(subItem.CERT);
                                postCount +=
                                  subItem.count - partialMergerFound?.count;
                                postDeposit +=
                                  subItem.sum_DEPSUMBR -
                                  partialMergerFound?.depsum;
                                postShare +=
                                  ((subItem.sum_DEPSUMBR -
                                    partialMergerFound?.depsum || 0) /
                                    totals.DEPOSITS) *
                                  100;
                              }
                              postMerge = {
                                ...subItem,
                                count:
                                  subItem.count - partialMergerFound?.count,
                                sum_DEPSUMBR:
                                  subItem.sum_DEPSUMBR -
                                  partialMergerFound?.depsum,
                                POST: true,
                              };
                              partialMerges[subItem.CERT] = subItem.count;
                            } else if (!certsToMerge.includes(subItem.CERT)) {
                              if (!certList.includes(subItem.CERT)) {
                                certList.push(subItem.CERT);
                                postCount += subItem.count;
                                postDeposit += subItem.sum_DEPSUMBR;
                                postShare += subItem.SHARE;
                              }
                              postMerge = subItem;
                            }
                            return postMerge;
                          })
                          .filter((data: KeyValue) => data),
                        ASSET: data.ASSET,
                        count: postCount,
                        sum_DEPSUMBR: postDeposit,
                        SHARE: postShare,
                        SQUARED: postShare * postShare,
                        POST: true,
                      };
                    }
                    return filteredData;
                  })
                  .filter((data: KeyValue) => data),

                ASSET: data.ASSET,
                count: count,
                sum_DEPSUMBR: deposit,
                SHARE: share,
                SQUARED: share * share,
                PARTIALBANK: partial,
              };
            }

            if (Object.keys(postMerger).length) {
              partialMerges[data.RSSDHCR] = data.count + postMerger.count;
              postMergerData.push(postMerger);
            }
            return data;
          })
          .filter((data) => Object.keys(data).length > 0);
        setPartialMergeCount(partialMerges);
        //This begins the merging of institutions without a BHC into an acquiring BHC
        let noBHCDataMerge: KeyValue[] = [];
        let postMergerRow: KeyValue = {};
        let partialMergerRow: KeyValue = {};
        let postMergerRows: KeyValue[] = [{}];
        let noBhcPostMergerRows: KeyValue[] = [];
        noBHCData.forEach((obj: KeyValue) => {
          if (certsToMerge.includes(obj.CERT)) {
            noBHCDataMerge.push(obj);
          }
        });
        setNoBHCDataToMerge(noBHCDataMerge);
        setPrevTotals(totals);
        noBHCData.length && setPrevNoBHCData(noBHCData);

        if (noBHCDataMerge.length) {
          let count = 0;
          let deposit = 0;
          let share = 0;
          let asset = 0;
          noBHCDataMerge = noBHCDataMerge.map((obj: KeyValue) => {
            const noBhcPartialMergerFound = partialMergerSelections.find(
              (merger) => merger.cert === obj.CERT,
            );
            if (noBhcPartialMergerFound) {
              partialMerges[obj.CERT] = obj.count;
              const share =
                ((obj.sum_DEPSUMBR - noBhcPartialMergerFound.depsum) /
                  totals.DEPOSITS) *
                100;
              noBhcPostMergerRows.push({
                ...obj,
                count: obj.count - noBhcPartialMergerFound.count,
                sum_DEPSUMBR: obj.sum_DEPSUMBR - noBhcPartialMergerFound.depsum,
                SHARE: share,
                SQUARED: share * share,
                POST: true,
              });
              count += noBhcPartialMergerFound.count;
              deposit += noBhcPartialMergerFound.depsum;
              asset += obj.ASSET;
              const newShare =
                (noBhcPartialMergerFound.depsum / totals.DEPOSITS) * 100;
              return {
                ...obj,
                count: noBhcPartialMergerFound.count,
                sum_DEPSUMBR: noBhcPartialMergerFound.depsum,
                SHARE: newShare,
                SQUARED: newShare * newShare,
                PARTIALBANK: 1,
                PARTIALMERGECOUNT: obj.count,
                noBHCMerge: true,
              };
            } else {
              count += obj.count;
              deposit += obj.sum_DEPSUMBR;
              asset += obj.ASSET;
              return { ...obj, OLDBANKS: 1, noBHCMerge: true };
            }
          });
          share = (deposit / totals.DEPOSITS) * 100;

          const filteredData = noBHCData.filter(
            (item) =>
              !noBHCDataMerge.some((mergeItem) => mergeItem.CERT === item.CERT),
          );
          setNoBHCData(
            sortObjValue(
              [...filteredData, ...noBhcPostMergerRows],
              "sum_DEPSUMBR",
              true,
            ),
          ); //A new row has to be created which represents the Institutions's BHC (if it didn't have one before)
          certsToMergeRows.push({
            RSSDHCR: "",
            NAMEFULL: "Institutions without BHC",
            STALP: "",
            count: count,
            sum_DEPSUMBR: deposit,
            SHARE: share,
            SQUARED: share * share,
            ASSET: asset,
            subData: sortObjValue(noBHCDataMerge, "sum_DEPSUMBR", true),
            noMergerStatus: true,
          });
        }
        certsToMergeRows.unshift(acquiringRow[0]);
        let oldSquaredMarketShare = 0;
        certsToMergeRows.forEach(
          (item: KeyValue) =>
            (oldSquaredMarketShare = oldSquaredMarketShare + item.SQUARED),
        );
        setPartialMergeCount(partialMerges);
        const certs = certsToMerge;
        certs.push(...certAcquiring);
        const mergerObj: KeyValue = {};
        if (
          partialMergerFound &&
          partialMergerSelections.length > 0 &&
          !isBhc
        ) {
          for (const data of partialMergerSelections) {
            const mergerData = pmMatchingTableData.find(
              (obj: KeyValue) => obj.CERT === data.cert,
            );
            if (mergerData?.count && data.cert) {
              postMergerRow = {
                POST: true,
                ASSET: mergerData.ASSET,
                BKCLASS: mergerData.BKCLASS,
                CERT: mergerData.CERT,
                CHARTER: mergerData.CHARTER,
                NAMEFULL: mergerData.NAMEFULL,
                STALP: mergerData.STALP,
                count: mergerData.count - data.count,
                sum_DEPSUMBR: mergerData.sum_DEPSUMBR - data.depsum,
                SHARE:
                  ((mergerData.sum_DEPSUMBR - data.depsum) / totals.DEPOSITS) *
                  100,
              };
              postMergerRow.SQUARED = postMergerRow.SHARE * postMergerRow.SHARE;
              mergerObj[mergerData.CERT] = mergerData.count;

              partialMergerRow = {
                PARTIALBANK: 1,
                PARTIALMERGECOUNT: mergerData.count,
                ASSET: mergerData.ASSET,
                BKCLASS: mergerData.BKCLASS,
                CERT: mergerData.CERT,
                CHARTER: mergerData.CHARTER,
                NAMEFULL: mergerData.NAMEFULL,
                STALP: mergerData.STALP,
                count: mergerData.count - postMergerRow["count"],
                sum_DEPSUMBR:
                  mergerData.sum_DEPSUMBR - postMergerRow["sum_DEPSUMBR"],
                SHARE:
                  ((mergerData.sum_DEPSUMBR - postMergerRow["sum_DEPSUMBR"]) /
                    totals.DEPOSITS) *
                  100,
              };

              partialMergerRow["SQUARED"] =
                partialMergerRow["SHARE"] * partialMergerRow["SHARE"];

              certsToMergeRows.push(partialMergerRow);
              postMergerRows.push(postMergerRow);
              certs.push(data["cert"]);
            }
          }
          setPartialMergeCount(mergerObj);
        }

        const oldInstitutionRows: KeyValue[] = certsToMergeRows.map((obj) => {
          return { ...obj, OLDBANKS: 1 };
        });
        certsToMergeRows.forEach((entry: KeyValue) => {
          summation.count += entry.count;
          summation.sum_DEPSUMBR += entry.sum_DEPSUMBR;
          summation.SHARE += entry.SHARE;
        });
        const value = acquiringRow[0].hasOwnProperty("CERT")
          ? "CERT"
          : "RSSDHCR";
        const newInstitutionRow: KeyValue = {
          NEWBANK: 1,
          [value]: acquiringRow[0][value],
          NAMEFULL: acquiringRow[0].NAMEFULL,
          STALP: acquiringRow[0].STALP,
          BKCLASS: acquiringRow[0].BKCLASS,
          CHARTER: acquiringRow[0].CHARTER,
          ASSET: acquiringRow[0].ASSET,
          SQUARED: summation.SHARE * summation.SHARE,
          ...summation,
        };

        const newTableData: KeyValue[] = tableData.filter(
          (item: KeyValue) => !certs.includes(item[key]),
        );
        postMergerRows = postMergerRows.filter((obj: KeyValue) =>
          obj.hasOwnProperty("count"),
        );

        partialMergerFound && newTableData.push(...postMergerRows);
        newTableData.push(newInstitutionRow);

        const allData: KeyValue[] = sortObjValue(
          [...postMergerData, ...newTableData],
          "sum_DEPSUMBR",
          true,
        );

        if (oldInstitutionRows[0]?.hasOwnProperty("OLDBANKS")) {
          oldInstitutionRows[0].OLDBANKS = 2;
        }

        allData.forEach((data) => {
          if (data.hasOwnProperty("NEWBANK")) {
            data.old = oldInstitutionRows;
          }
        });

        setTableData(allData);
        isBhc && setWithBhc(true);
      }
    } else {
      resetReportData();
    }
  };

  //Download
  const additionalHeaders = reportFinalized ? ["Acquiring / Merged"] : [];

  const tableDownloadHeaders = [
    "Reporting Period",
    ...additionalHeaders,
    isBhc ? "Holding Company / Institution Name" : "Institution Name",
    isBhc ? "RSSDHCR / CERT" : "CERT",
    "State Headquarters",
    "Bank Class",
    "Charter",
    `Matching Assets as of ${reportMonth}/${reportDay}/${reportYear} (Thousands USD)`,
    "Office Count",
    isBhc
      ? "Matching Combined Deposits (Thousands USD)*"
      : "Matching Deposits (Thousands USD)",
    "Market Share",
    "Squared Market Share (HHI)",
  ];

  const tableDownloadHeadersNoBhc = [
    "Reporting Period",
    ...additionalHeaders,
    noBHCAcqBHC && !withBhc ? "Institution / BHC Name" : "Institution Name",
    noBHCAcqBHC && !withBhc ? "CERT / RSSDHCR" : "CERT",
    "State (HQ)",
    "Bank Class",
    "Charter",
    `Matching Assets as of ${reportMonth}/${reportDay}/${reportYear} (Thousands USD)`,
    "Office Count",
    "Matching Deposits (Thousands USD)",
    "Market Share",
    "Squared Market Share (HHI)",
  ];

  const tableDownloadHeadersTotalOrg = [
    "Scope",
    "Matching Office Count",
    "Total Combined Deposits (Thousands USD)",
    "Market Share",
    "HHI",
  ];

  const totalBankOrg: any = [];

  const scopeTotalsArray: string[] = [
    "BHCs and Subsidiaries",
    "Institutions without BHC",
    "Totals",
  ];

  let downloadData: KeyValue[] = [];
  tableData.forEach((data: KeyValue) => {
    downloadData.push(data);
    if (data.old) {
      data.old.forEach((oldData: KeyValue) => {
        downloadData.push(oldData);
        if (oldData.subData) {
          oldData.subData.forEach((subData: KeyValue) =>
            downloadData.push(subData),
          );
        }
      });
    }
    if (data.subData) {
      data.subData.forEach((subData: KeyValue) => downloadData.push(subData));
    }
  });
  let noBHCDownloadData: KeyValue[] = [];
  noBHCData?.forEach((data: KeyValue) => {
    noBHCDownloadData.push(data);
    if (data.old) {
      data.old.forEach((oldData: KeyValue) => {
        noBHCDownloadData.push(oldData);
        if (oldData.subData) {
          oldData.subData.forEach((subData: KeyValue) =>
            noBHCDownloadData.push(subData),
          );
        }
      });
    }
    if (data.subData) {
      data.subData.forEach((subData: KeyValue) => downloadData.push(subData));
    }
  });

  const tableDownloadData: KeyValue[] = downloadData.map((item: KeyValue) => {
    let additionalDataRows: string[] = [];
    if (item.NEWBANK) {
      additionalDataRows =
        isBhc && withBhc && !noBHCAcqBHC
          ? ["Acquiring BHC"]
          : ["New Institution"];
    } else if (item.OLDBANKS && item.OLDBANKS === 2) {
      additionalDataRows =
        isBhc && withBhc && !noBHCAcqBHC ? ["Old BHC"] : ["Old Institution"];
    } else if (item.PARTIALBANK && item.PARTIALBANK === 1) {
      additionalDataRows = ["Partial Merger"];
    } else if (item.OLDBANKS && item.OLDBANKS === 1) {
      additionalDataRows = ["Merged"];
    } else if (item.POST) {
      additionalDataRows = ["Post-Merger"];
    } else if (reportFinalized) {
      additionalDataRows = [""];
    }

    const mkt = Number((item.sum_DEPSUMBR / totals.DEPOSITS) * 100);
    return [
      `6/30/${appState.filters.lastYear}`,
      ...additionalDataRows,
      item.NAMEFULL ?? "Totals",
      item.CERT ?? item.RSSDHCR,
      item.STALP,
      item.BKCLASS,
      item.CHARTER,
      Number(item.ASSET)?.toLocaleString(),
      Number(item.count)?.toLocaleString(),
      Number(item.sum_DEPSUMBR)?.toLocaleString(),
      `${mkt.toFixed(2)}%`,
      Number(mkt * mkt)
        ?.toFixed(4)
        .toLocaleString(),
    ];
  });

  const tableDownloadDataNoBHCs: KeyValue[] = noBHCDownloadData
    ? noBHCDownloadData.map((item: KeyValue) => {
        let additionalDataRows: string[] = [];
        if (item.NEWBANK) {
          additionalDataRows =
            isBhc && withBhc && !noBHCAcqBHC
              ? ["Acquiring BHC"]
              : ["New Institution"];
        } else if (item.OLDBANKS && item.OLDBANKS === 2) {
          additionalDataRows =
            isBhc && withBhc && !noBHCAcqBHC
              ? ["Old BHC"]
              : ["Old Institution"];
        } else if (noBHCAcqBHC && item.OLDBANKS === 3) {
          additionalDataRows = ["Old Institution"];
        } else if (
          noBHCAcqBHC &&
          item.hasOwnProperty("RSSDHCR") &&
          !item.hasOwnProperty("CERT") &&
          !item.PARTIALBANK &&
          !item.noMergerStatus
        ) {
          additionalDataRows = ["Merged BHC"];
        } else if (
          noBHCAcqBHC &&
          item.hasOwnProperty("CERT") &&
          item.OLDBANKS &&
          item.OLDBANKS === 1 &&
          !item.PARTIALBANK
        ) {
          additionalDataRows = ["Merged Institution"];
        } else if (item.PARTIALBANK && item.PARTIALBANK === 1) {
          additionalDataRows = ["Partial Merger"];
        } else if (
          item.OLDBANKS &&
          item.OLDBANKS === 1 &&
          !item.noMergerStatus
        ) {
          additionalDataRows = ["Merged"];
        } else if (item.POST) {
          additionalDataRows = ["Post-Merger"];
        } else if (reportFinalized) {
          additionalDataRows = [""];
        }

        const mkt = Number((item.sum_DEPSUMBR / totals.DEPOSITS) * 100);
        return [
          `6/30/${appState.filters.lastYear}`,
          ...additionalDataRows,
          item.NAMEFULL ?? "Totals",
          item.hasOwnProperty("CERT") ? item.CERT : item.RSSDHCR,
          item.STALP,
          item.BKCLASS,
          item.CHARTER,
          Number(item.ASSET).toLocaleString(),
          Number(item.count).toLocaleString(),
          Number(item.sum_DEPSUMBR).toLocaleString(),
          `${mkt.toFixed(2)}%`,
          Number(mkt * mkt)
            .toFixed(4)
            .toLocaleString(),
        ];
      })
    : [];

  const noBHCTotals: KeyValue =
    noBHCData.length > 0 ? arraySum(noBHCData) : { count: 0, sum_DEPSUMBR: 0 };

  const proFormaSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "MSA",
      value: appState.filters.msa ? appState.filters.msa.toString() : "",
      formKey: "msa",
      static: true,
    },
    {
      label: "ZIP",
      value: appState.filters.zipbr ? appState.filters.zipbr.toString() : "",
      formKey: "zipbr",
      static: true,
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={proFormaSearchPills} />}
    />
  );

  const institutionsCount = tableData.filter(
    (data: KeyValue) => !data.OLDBANKS,
  ).length;

  const finalizeResetButton = (
    <Typography
      className={classes.bannerStyles}
      variant="body1"
      style={{ textAlign: "center" }}
      component="div"
    >
      <Button
        id="finalize-report-button"
        onClick={finalizeReport}
        variant="contained"
        disableElevation
        className={classes.finalizeResetButton}
        disabled={
          !(
            certAcquiring[0] &&
            (certsToMerge.length !== 0 ||
              partialMergerSelections.some((obj: KeyValue) =>
                obj.hasOwnProperty("cert"),
              ))
          )
        }
      >
        {reportFinalized ? "Reset report" : "Finalize Report"}
      </Button>
    </Typography>
  );
  const resetReportBanner = (
    <Typography
      className={classes.backgroundColorBanner}
      style={{ maxHeight: "100px" }}
      component="div"
    >
      <Typography
        className={classes.bannerStyles}
        variant="body1"
        style={{ fontWeight: "bold", paddingTop: "5px", fontSize: "20px" }}
      >
        Your proposed recombinations are below.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={8}>
          <Typography className={classes.bannerStyles} variant="body1">
            Download your results and rerun with new parameters.
          </Typography>
        </Grid>
        <Grid size={4}>{finalizeResetButton}</Grid>
      </Grid>
    </Typography>
  );

  const finalizeReportBanner = (
    <Typography className={classes.backgroundColorBanner} component="div">
      <Typography
        className={classes.bannerStyles}
        variant="body1"
        style={{ fontWeight: "bold", paddingTop: "5px", fontSize: "20px" }}
      >
        Make your proposed recombinations below, then finalize the report.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={8}>
          <Typography className={classes.bannerStyles} variant="body1">
            Create a proposed new bank and see how the resulting merger affects
            the market share of your selected geographic market. You must pick
            one “acquiring” institution and any number of whole or partial
            institutions (by selecting individual branch offices) to be merged.
            Click the “Finalize Report” button to see the results.
          </Typography>
        </Grid>
        <Grid size={4}>{finalizeResetButton}</Grid>
      </Grid>
    </Typography>
  );

  const totalBankOrgs = loading
    ? "0"
    : (institutionsCount + (noBHCData?.length || 0))?.toLocaleString();

  const orgTitle = isBhc ? (
    <>
      <br />
      <br />
      <Typography className={classes.holdingCompanyTitleStyle}>
        <b>Total Bank Organizations in Market: {totalBankOrgs}</b>
      </Typography>
    </>
  ) : (
    <></>
  );

  const formatHHI = (value: number) =>
    value?.toLocaleString(undefined, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });

  const bhcOrgTableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: "Selected Market",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 4,
      },
    ],
    (() => {
      const headerValues = [
        { title: "Office Count" },
        {
          title: "Total Deposits (Thousands USD)",
          styles: { textAlign: "right" },
        },
      ];

      const institutionHeader: TableHeader[] = [
        {
          title: "Table Filter",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
          },
          colSpan: 1,
          apiField: "",
        },
        {
          title: "Scope",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
          },
          colSpan: 1,
          apiField: "",
        },
      ];
      const selectedMarket: TableHeader[] = headerValues.map(
        (header: KeyValue) => {
          return {
            title: header.title,
            styles: {
              backgroundColor: additionalHeaderColor,
              color: textHeaderColor,
              ...header.styles,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const marketShare: TableHeader[] = [
        {
          title: "Market Share (%)",
          styles: {
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      const hhiColumn: TableHeader[] = [
        {
          title: "HHI",
          styles: {
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      return [
        ...institutionHeader,
        ...selectedMarket,
        ...marketShare,
        ...hhiColumn,
      ];
    })(),
  ];
  const withBhcHHI = tableData.reduce(
    (sum: number, item: KeyValue) => sum + (item.SQUARED ?? 0),
    0,
  );
  const postMergerHHITotal: number = withBhcHHI + (hhiNoBHC ?? 0);
  const totalsBHC = !reportFinalized ? totals?.HHI : withBhcHHI;
  const totalsNoBHC = hhiNoBHC;
  const totalHHICalc = totalsBHC + totalsNoBHC;
  const totalsHHI = [
    formatHHI(totalsBHC),
    formatHHI(totalsNoBHC),
    formatHHI(totalHHICalc),
  ];

  const totalOrgTable: React.ReactElement[] =
    isBhc && totals.hasOwnProperty("DEPOSITS")
      ? [
          <React.Fragment key={"noBHCTotals"}>
            <TableRow>
              <TableCell colSpan={1}>
                <Button
                  id="viewBhcTable"
                  color={withBhc ? "primary" : "secondary"}
                  variant="contained"
                  className={classes.viewResultsButton}
                  disabled={!(tableData || tableData[0] || totals?.DEPOSITS)}
                  disableElevation
                  onClick={() => {
                    setWithBhc(true);
                    updatePage(1);
                  }}
                >
                  View Results
                </Button>
              </TableCell>
              <TableCell colSpan={1} style={{ textAlign: "left" }}>
                BHCs and Subsidiaries
              </TableCell>
              <TableCell colSpan={1}>
                {(totals?.OFFICES - noBHCTotals?.count)?.toLocaleString()}
              </TableCell>
              <TableCell colSpan={1} style={{ textAlign: "right" }}>
                {(
                  totals.DEPOSITS - noBHCTotals?.sum_DEPSUMBR
                )?.toLocaleString()}
              </TableCell>
              <TableCell colSpan={1}>
                {totals.DEPOSITS && noBHCData.length
                  ? (
                      (100 *
                        (Number(totals.DEPOSITS) - noBHCTotals?.sum_DEPSUMBR ||
                          0)) /
                      totals.DEPOSITS
                    ).toFixed(2)
                  : "100.00"}
                %
              </TableCell>
              <TableCell colSpan={1}>
                {!reportFinalized
                  ? formatHHI(totals?.HHI)
                  : formatHHI(withBhcHHI)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={1}>
                <Button
                  id="viewNoBhcTable"
                  color={!withBhc ? "primary" : "secondary"}
                  variant="contained"
                  className={classes.viewResultsButton}
                  disableElevation
                  disabled={!noBHCTotals?.count}
                  onClick={() => {
                    setWithBhc(false);
                    updatePage(1);
                  }}
                >
                  View Results
                </Button>
              </TableCell>
              <TableCell colSpan={1} style={{ textAlign: "left" }}>
                Institutions without BHC
              </TableCell>
              <TableCell colSpan={1}>
                {noBHCTotals.count?.toLocaleString()}
              </TableCell>
              <TableCell colSpan={1} style={{ textAlign: "right" }}>
                {noBHCTotals.sum_DEPSUMBR?.toLocaleString()}
              </TableCell>
              <TableCell colSpan={1}>
                {totals.DEPOSITS && noBHCTotals.sum_DEPSUMBR && noBHCData.length
                  ? (
                      (100 * (Number(noBHCTotals.sum_DEPSUMBR) || 0)) /
                      totals.DEPOSITS
                    ).toFixed(2)
                  : "0.00"}
                %
              </TableCell>
              <TableCell>{formatHHI(hhiNoBHC)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={2}
                className={classes.firstCellDarkest}
                style={{
                  textAlign: "left",
                  backgroundColor: primaryHeaderColor,
                  color: textHeaderColor,
                  fontWeight: "bold",
                }}
              >
                Totals
              </TableCell>
              <TableCell
                colSpan={1}
                className={classes.firstCellDarkest}
                style={{
                  backgroundColor: additionalHeaderColor,
                  color: textHeaderColor,
                  fontWeight: "bold",
                }}
              >
                {totals.OFFICES?.toLocaleString()}
              </TableCell>
              <TableCell
                style={{
                  textAlign: "right",
                  backgroundColor: additionalHeaderColor,
                  color: textHeaderColor,
                  fontWeight: "bold",
                }}
              >
                {totals.DEPOSITS?.toLocaleString()}
              </TableCell>
              <TableCell
                style={{
                  textAlign: "center",
                  backgroundColor: additionalHeaderColor,
                  color: textHeaderColor,
                  fontWeight: "bold",
                }}
              >
                100.00%
              </TableCell>
              <TableCell
                style={{
                  backgroundColor: additionalHeaderColor,
                  color: textHeaderColor,
                }}
              >
                {formatHHI(totalHHICalc)}
              </TableCell>
            </TableRow>
          </React.Fragment>,
        ]
      : [<React.Fragment key={"noBHCTotals"}></React.Fragment>];

  const institutionsBanner = () => {
    const banner = (
      <Typography
        className={classes.bannerStyles}
        variant="body2"
        style={{ fontWeight: "bold", fontSize: "26px", paddingTop: "8px" }}
      >
        {isBhc && !withBhc && noBHCData.length
          ? `Institutions without Holding Companies in Market: ${noBHCData.length?.toLocaleString()}`
          : isBhc && withBhc
            ? `Holding Companies with Subsidiaries in Market: ${institutionsCount?.toLocaleString()}`
            : `Number of Institutions in Market: ${institutionsCount?.toLocaleString()}`}
      </Typography>
    );
    return banner;
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setPeriod(reportPeriod);
    setAnchorEl(null);
  };

  const handleDone = () => {
    setOnlyFinancials(true);
    setReportPeriod(period);
    setGetFinancials(true);
    setAnchorEl(null);
  };
  const tableRowOnClick = (
    cert: string,
    name: string,
    int: number,
    bhcRow: boolean,
    isCount: boolean = false,
  ) => {
    const onClickFn = (e: KeyValue) => {
      setRowIsBHC(bhcRow && withBhc);
      setName(name);
      setCert(cert);
      if (e.key && e.key !== "Enter") {
        return;
      }
      if (
        (appState.filters.msa ||
          appState.filters.zipbr ||
          appState.filters.stateNames) &&
        appState.filters.lastYear &&
        certAcquiring[0] &&
        !reportFinalized &&
        (!bhcRow || !withBhc || isCount)
      ) {
        setOpenModal(cert);
      }
    };
    return {
      onClick: onClickFn,
      onKeyDown: onClickFn,
      tabIndex: 0,
    };
  };
  const rowStyle = (data: KeyValue, type?: string, subData?: boolean) => {
    let style: KeyValue;
    if (data.hasOwnProperty("NEWBANK") && reportFinalized) {
      style = {
        backgroundColor: isBhc ? "#afd4bd" : "#acc7df",
        fontWeight: "bold",
      };
    } else if ((type === "radio" || type === "checkbox") && subData) {
      style = {
        backgroundColor: "#afd4bd",
      };
    } else if (
      data.hasOwnProperty("OLDBANKS") &&
      reportFinalized &&
      !data.noBHCMerge
    ) {
      if (type) {
        style = {
          backgroundColor: isBhc ? "#d8eadf" : "#bed8db",
        };
      } else {
        style = {
          backgroundColor: isBhc && !subData ? "#d8eadf" : "#cddede",
        };
      }
    } else if (data.hasOwnProperty("PARTIALMERGECOUNT")) {
      style = {
        whiteSpace: "nowrap",
      };
    } else if (
      data.hasOwnProperty("POST") &&
      data.POST &&
      type === "checkbox"
    ) {
      style = { fontStyle: "italic" };
    } else {
      style = {};
    }
    return style;
  };
  const hhiTooltip = (labelText: string) => {
    return (
      <MoreInfoButton
        id="tooltip-HHI"
        popperPosition="left"
        label="HHI Calculation"
        hover
        HHICalculation={labelText}
      >
        <Container
          style={{
            maxWidth: "880px",
            maxHeight: "368px",
            padding: "10px",
          }}
        >
          <TableRow>
            <TableCell>
              <Typography className={classes.tooltipHHI}>
                The Herfindahl-Hirschman Index
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography className={classes.tooltipHHIBody}>
                "HHI" means the Herfindahl-Hirschman Index, a commonly accepted
                measure of market concentration. It is calculated by squaring
                the market share of each firm competing in the market and then
                summing the resulting numbers. For example, for a market
                consisting of four firms with shares of 30, 30, 20, and 20
                percent, the HHI is 2,600 (30<sup>2</sup> + 30<sup>2</sup> + 20<sup>2</sup> + 20<sup>2</sup> = 2,600).
              </Typography>
              <Typography className={classes.tooltipHHIBody}>
                The HHI takes into account the relative size and distribution of
                the firms in a market and approaches zero when a market consists
                of a large number of firms of relatively equal size. The HHI
                increases both as the number of firms in the market decreases
                and as the disparity in size between those firms increases.
              </Typography>
              <Typography className={classes.tooltipHHIBody}>
                Markets in which the HHI is between 1,000 and 1,800 points are
                considered to be moderately concentrated and those in which the
                HHI is in excess of 1,800 points are considered to be
                concentrated. Transactions that increase the HHI by more than
                100 points in concentrated markets presumptively raise antitrust
                concerns under the Horizontal Merger Guidelines issued by the
                U.S. Department of Justice and the Federal Trade Commission. See
                Merger Guidelines § 1.51.
              </Typography>
            </TableCell>
          </TableRow>
        </Container>
      </MoreInfoButton>
    );
  };

  const open = Boolean(anchorEl);

  const setAssetDate: (reportingPeriodObj: KeyValue) => void = (
    reportingPeriodObj: KeyValue,
  ) => {
    setPeriod(reportingPeriodObj.reportPeriod[0]);
  };

  const assetsDateSelectorButton = (
    <div key={1}>
      <Button
        id="assets-date-selector"
        aria-describedby={"simple-popover-button"}
        onClick={handleOpen}
        variant="contained"
        disableElevation
        color="secondary"
        style={{ margin: "0px 10px" }}
        endIcon={<MoreHorizIcon />}
      >
        {reportMonth}/{reportDay}/{reportYear}
      </Button>
      <Popover
        id={"simple-popover"}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transitionDuration={10}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        slots={{
          transition: Collapse,
        }}
      >
        <div id="selectable-details-display">
          <Paper
            sx={{
              padding: "1rem",
              minWidth: 400,
            }}
          >
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography
                  sx={(theme) => ({
                    textAlign: "center",
                    color: theme.palette.primary.main,
                    padding: 0,
                    marginBottom: "1rem",
                    marginTop: "1rem",
                    borderBottom: `1px solid ${theme.palette.secondary.main}`,
                  })}
                  variant="body2"
                >
                  Total Assets Report Date
                </Typography>
                <ReportingPeriod
                  inverse
                  currentQuarterValue={`${reportMonth}${reportDay}`}
                  currentYearValue={reportYear}
                  setForm={setAssetDate}
                  form={{
                    reportPeriod: [reportPeriod],
                  }}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                "marginTop": "1rem",
                "width": "100%",
                "textAlign": "center",
                "& button.MuiButton-root.MuiButton-containedSecondary": {
                  marginTop: 14,
                  marginBottom: 0,
                  border: "1px solid rgba(26, 68, 128, 0.5)",
                  width: 165,
                  height: 32,
                  fontSize: 13,
                },
              }}
            >
              <Button
                id="selectable-details-cancel"
                onClick={handleClose}
                variant="contained"
                disableElevation
                color="secondary"
                style={{ margin: "0px 10px" }}
              >
                Cancel
              </Button>
              <Button
                id="selectable-details-done"
                onClick={handleDone}
                variant="contained"
                disableElevation
                color="secondary"
                style={{ margin: "0px 10px" }}
              >
                Done
              </Button>
            </Box>
          </Paper>
        </div>
      </Popover>
    </div>
  );

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: "Acquiring",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "Merged",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
      {
        title:
          isBhc && withBhc && !noBHCAcqBHC
            ? "Holding Company / Institution Name"
            : noBHCAcqBHC && !withBhc
              ? "Institution / BHC Name"
              : "Institution Name",
        styles: headerStyle(primaryHeaderColor, "left", "", 155),
        colSpan: 1,
      },
      {
        title:
          isBhc && withBhc && !noBHCAcqBHC
            ? "RSSDHCR / CERT"
            : noBHCAcqBHC && !withBhc
              ? "CERT / RSSDHCR"
              : "CERT",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "State Headquarters",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "Bank Class",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "Charter",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: (
          <>Matching Assets as of {assetsDateSelectorButton} (Thousands USD)</>
        ),
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "Office Count",
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 1,
      },
      {
        title:
          isBhc && withBhc
            ? "Matching Combined Deposits (Thousands USD)*"
            : "Matching Deposits (Thousands USD)",
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "Market Share",
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 1,
      },
      {
        title: "Squared Market Share (HHI)",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 1,
      },
    ],
  ];

  const tableBody: React.ReactElement[] = tableData
    ? paginatedData.map((tableData: KeyValue, int: number) => {
        const tableRows = (
          data: KeyValue,
          bhcRow: boolean,
          subData?: boolean,
        ) => {
          const altColors =
            int % 2 !== 0
              ? classes.secondCellDarken
              : classes.secondCellDarkest;
          const cellClass =
            bhcRow && withBhc ? classes.bhcCellStyle : altColors;
          const altColor =
            subData && reportFinalized && tableData.old && isBhc
              ? { backgroundColor: "#eff6f2" }
              : {};

          const rowKey = bhcRow && withBhc ? "RSSDHCR" : "CERT";
          const bhcRowStyle = bhcRow && withBhc ? classes.bhcCellStyle : "";

          let bhcIndeterminate =
            !reportFinalized &&
            bhcRow &&
            data?.subData?.some((bank: KeyValue) =>
              certsToMerge.includes(bank.CERT),
            ) &&
            !data.subData.every((bank: KeyValue) =>
              certsToMerge.includes(bank.CERT),
            );
          if (
            data.subData &&
            partialMergerSelections.length > 0 &&
            !reportFinalized &&
            bhcRow
          ) {
            bhcIndeterminate = data.subData.some((bank: KeyValue) =>
              partialMergerSelections
                .map((merger) => merger.cert)
                .includes(bank.CERT),
            );
          }

          const bhcPartialCount =
            !reportFinalized &&
            bhcIndeterminate &&
            [
              ...data.subData.map(
                (bank: KeyValue) =>
                  !partialMergerSelections.find(
                    (merge) => bank.CERT === merge.cert,
                  ) &&
                  certsToMerge.includes(bank.CERT) &&
                  bank.count,
              ),

              ...data.subData.map(
                (bank: KeyValue) =>
                  partialMergerSelections.find(
                    (merge) => bank.CERT === merge.cert,
                  )?.count,
              ),
            ]
              .filter((data: any) => data)
              .reduce((a: number, b: number) => a + b, 0);

          let mergerStatus = "";
          if (data.OLDBANKS && !data.noMergerStatus && !data.PARTIALBANK) {
            if (data.OLDBANKS === 2) {
              mergerStatus =
                isBhc && !noBHCAcqBHC ? "Old BHC" : "Old Institution";
            } else if (noBHCAcqBHC && data.OLDBANKS === 3) {
              mergerStatus = "Old Institution";
            } else if (noBHCAcqBHC && data.hasOwnProperty("RSSDHCR")) {
              mergerStatus = "Merged BHC";
            } else if (noBHCAcqBHC && data.hasOwnProperty("CERT")) {
              mergerStatus = "Merged Institution";
            } else {
              mergerStatus = "Merged";
            }
          } else if (data.PARTIALBANK && (isBhc ? bhcRow : true)) {
            mergerStatus = "Partial Merger";
          } else if (data.PARTIALBANK && data.noBHCMerge) {
            mergerStatus = "Partial Merger";
          } else if (data.POST && (bhcRow || !isBhc)) {
            mergerStatus = "Post-Merger";
          }

          let charterVal = "";
          if ((!bhcRow || !withBhc) && data.CHARTER) {
            charterVal =
              data.CHARTER.toLocaleString() === "FED" ? "Federal" : "State";
          }
          return (
            <TableRow
              ref={data.old && paginationRef}
              id={`${data[rowKey]}_${int}${data.old ? "_acquiring" : ""}`}
              key={`${data[rowKey]}_${int}${data.old ? "_acquiring" : ""}`}
              style={altColor}
              className={
                bhcRow && withBhc
                  ? ""
                  : int % 2 !== 0
                    ? classes.firstCellDarken
                    : classes.firstCellDarkest
              }
              sx={
                (!bhcRow || !withBhc) && certAcquiring[0] && !reportFinalized
                  ? {
                      "&:hover": {
                        "backgroundColor": "#C0DEDE",
                        "cursor": "pointer",
                        "& .MuiTableCell-root": {
                          backgroundColor: "#C0DEDE",
                        },
                      },
                    }
                  : {}
              }
            >
              <TableCell
                className={bhcRowStyle}
                style={{
                  ...rowStyle(
                    data,
                    "radio",
                    reportFinalized && tableData.old && isBhc,
                  ),
                  fontWeight: "bold",
                }}
              >
                {!reportFinalized ? (
                  !isBhc || (isBhc && bhcRow) ? (
                    <Radio
                      id={`${data[rowKey]}_${int}_radio`}
                      color="primary"
                      sx={{ "&:hover": { backgroundColor: "#FFFFFF" } }}
                      checked={selectedValue === `${data[rowKey]}_${int}`}
                      inputProps={{ "aria-label": "radio button" }}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleRadioButtonChange(
                          event,
                          data[rowKey],
                          bhcRow && data.subData
                            ? [
                                data.RSSDHCR,
                                ...data.subData.map(
                                  (subData: KeyValue) => subData.CERT,
                                ),
                              ]
                            : [data[rowKey]],
                          removeCert,
                          setSelectedValue,
                          setCertAcquiring,
                          partialMergerSelections,
                          setPartialMergerSelections,
                          updateUi,
                        )
                      }
                      value={`${data[rowKey]}_${int}`}
                      name="radio-buttons"
                    />
                  ) : (
                    <></>
                  )
                ) : data.hasOwnProperty("NEWBANK") ? (
                  isBhc && !noBHCAcqBHC ? (
                    "Acquiring BHC"
                  ) : (
                    "New Institution"
                  )
                ) : (
                  ""
                )}
              </TableCell>

              <TableCell
                className={bhcRowStyle}
                style={{
                  ...rowStyle(
                    data,
                    "checkbox",
                    reportFinalized && tableData.old && isBhc,
                  ),
                  fontWeight: "bold",
                }}
              >
                {!reportFinalized && (
                  <Checkbox
                    id={`${data[rowKey]}_${int}_checkbox`}
                    value={`${data[rowKey]}_${int}`}
                    disabled={
                      isBhc && !withBhc
                        ? selectedValue === `${data["CERT"]}_${int}`
                        : selectedValue === `${data[key]}_${int}`
                    }
                    checked={
                      bhcRow && data.subData
                        ? data.subData.every((bank: KeyValue) =>
                            certsToMerge.includes(bank["CERT"]),
                          )
                        : certsToMerge.includes(data["CERT"])
                    }
                    indeterminate={
                      bhcIndeterminate ||
                      partialMergerSelections.some(
                        (obj: KeyValue) => obj.cert === data.CERT,
                      )
                    }
                    inputProps={{ "aria-label": "checkbox" }}
                    sx={{ "&:hover": { backgroundColor: "#FFFFFF" } }}
                    onChange={(event: any) => {
                      handleCheckboxChange(
                        event,
                        subData
                          ? [data.CERT, data.RSSDHCR]
                          : bhcRow && data.subData
                            ? [
                                data["RSSDHCR"],
                                ...data.subData.map(
                                  (subData: KeyValue) => subData.CERT,
                                ),
                              ]
                            : [data[rowKey]],
                        partialMergerSelections,
                        setPartialMergerSelections,
                        updateUi,
                        appState,
                        addCert,
                        removeCert,
                      );
                    }}
                  />
                )}
                {mergerStatus}
              </TableCell>
              <TableCell
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
                className={bhcRowStyle}
                sx={
                  (!bhcRow || !withBhc) && certAcquiring[0] && !reportFinalized
                    ? {
                        "textDecoration": "underline",
                        "fontWeight": "bold",
                        ...rowStyle(data),
                        "&.MuiTableCell-root": {
                          color: "#0000EE",
                          textAlign: "left",
                        },
                      }
                    : {
                        "&.MuiTableCell-root": {
                          ...rowStyle(data),
                          textAlign: "left",
                        },
                      }
                }
              >
                {data.NAMEFULL}
              </TableCell>
              <TableCell
                className={bhcRowStyle}
                style={rowStyle(data)}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {data?.CERT || data.RSSDHCR}
              </TableCell>
              <TableCell
                className={bhcRowStyle}
                style={rowStyle(data)}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {data.STALP}
              </TableCell>
              <TableCell
                className={bhcRowStyle}
                style={rowStyle(data)}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {data.BKCLASS}
              </TableCell>
              <TableCell
                className={bhcRowStyle}
                style={rowStyle(data)}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {charterVal}
              </TableCell>
              <TableCell
                className={cellClass}
                style={{ ...rowStyle(data, "asset"), textAlign: "right" }}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {data.ASSET?.toLocaleString()}
              </TableCell>

              <TableCell
                id={`office-count-${data[rowKey]}`}
                {...(!reportFinalized && {
                  ...tableRowOnClick(
                    data[rowKey],
                    data.NAMEFULL,
                    int,
                    bhcRow,
                    true,
                  ),
                })}
                className={cellClass}
                style={reportFinalized ? { ...rowStyle(data, "count") } : {}}
                sx={
                  certAcquiring[0] && !reportFinalized
                    ? {
                        "textDecoration": "underline",
                        "fontWeight": "bold",
                        "&.MuiTableCell-root": {
                          color: "#0000EE",
                        },
                        "&:hover": {
                          backgroundColor: "#D9E8F6",
                          cursor: "pointer",
                        },
                      }
                    : {}
                }
              >
                {!reportFinalized &&
                  partialMergerSelections.length > 0 &&
                  partialMergerSelections.find(
                    (merge) => data.CERT === merge.cert,
                  ) !== undefined &&
                  `${
                    partialMergerSelections.find(
                      (merge) => data.CERT === merge.cert,
                    )?.count?.toLocaleString()
                  } of `}
                {bhcPartialCount && `${bhcPartialCount} of `}
                {data.PARTIALBANK && data.PARTIALBANK === 1
                  ? `${data.count?.toLocaleString()} of ${
                      partialMergeCount[data[rowKey] ?? data.RSSDHCR]?.toLocaleString()
                    }`
                  : ""}
                {!data.PARTIALBANK && data.count?.toLocaleString()}
                {data.POST && ` of ${partialMergeCount[data[rowKey]]?.toLocaleString()}`}
              </TableCell>
              <TableCell
                style={{ ...rowStyle(data, "depsumbr"), textAlign: "right" }}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
                className={cellClass}
              >
                {data.sum_DEPSUMBR?.toLocaleString()}
              </TableCell>
              <TableCell
                className={cellClass}
                style={rowStyle(data, "share")}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {data.SHARE?.toFixed(2)}%
              </TableCell>
              <TableCell
                className={bhcRowStyle}
                style={rowStyle(data)}
                {...(!reportFinalized && {
                  ...tableRowOnClick(data[rowKey], data.NAMEFULL, int, bhcRow),
                })}
              >
                {formatHHI(data.SHARE * data.SHARE)}
              </TableCell>
            </TableRow>
          );
        };

        return (
          <React.Fragment
            key={`${tableData[isBhc ? "RSSDHCR" : "CERT"]}_${int}`}
          >
            {tableRows(tableData, isBhc)}
            {tableData.subData &&
              tableData.subData.map((subData: KeyValue) =>
                tableRows(subData, false, true),
              )}
            {tableData.old &&
              tableData.old.map((old: KeyValue, newInt: number) => {
                return (
                  <React.Fragment
                    key={`${
                      tableData[isBhc ? "RSSDHCR" : "CERT"]
                    }_${int}_${newInt}`}
                  >
                    {tableRows(old, isBhc)}
                    {old.subData &&
                      old.subData.map((subData: KeyValue) =>
                        tableRows(subData, false, true),
                      )}
                  </React.Fragment>
                );
              })}
          </React.Fragment>
        );
      })
    : [
        <tr key="no-results">
          <td colSpan={7}>
            Double check your search criteria for errors and try your search
            again.
          </td>
        </tr>,
      ];
  const preMergerHHITotal: number = prevNoBHCDataHHI + totals.HHI;
  const hhiRows = (
    <>
      <TableRow id="hhiDescr" key="hhiDescr">
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        {reportFinalized && (
          <>
            <TableCell
              colSpan={4}
              style={{
                fontWeight: "bold",
                textAlign: "right",
                alignItems: "right",
                flex: "nowrap",
              }}
            >
              {hhiTooltip("HHI Calculations")}
            </TableCell>
            <TableCell
              colSpan={3}
              style={headerStyle(primaryHeaderColor, "right")}
            >
              HHI Description
            </TableCell>
            <TableCell style={headerStyle(primaryHeaderColor, "center")}>
              Value
            </TableCell>
          </>
        )}
      </TableRow>

      {reportFinalized && (
        <>
          <TableRow id="preMergerHHI" key="preMergerHHI">
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell
              colSpan={3}
              style={{
                fontWeight: "bold",
                textAlign: "right",
                alignItems: "right",
                flex: "nowrap",
                backgroundColor: "#ccc",
              }}
            >
              Pre-Merger HHI
            </TableCell>
            <TableCell style={headerStyle(primaryHeaderColor, "center")}>
              {formatHHI(preMergerHHITotal)}
            </TableCell>
          </TableRow>
          <TableRow id="postMergerHHI" key="postMergerHHI">
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell
              colSpan={3}
              style={{
                fontWeight: "bold",
                textAlign: "right",
                alignItems: "right",
                flex: "nowrap",
                backgroundColor: "#ccc",
              }}
            >
              Post-Merger HHI
            </TableCell>
            <TableCell style={headerStyle(primaryHeaderColor, "center")}>
              {formatHHI(postMergerHHITotal)}
            </TableCell>
          </TableRow>
          <TableRow id="changeHHI" key="changeHHI">
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell
              colSpan={3}
              style={{
                fontWeight: "bold",
                textAlign: "right",
                alignItems: "right",
                flex: "nowrap",
                backgroundColor: "#ccc",
              }}
            >
              Change in HHI Points
            </TableCell>
            <TableCell style={headerStyle(primaryHeaderColor, "center")}>
              {formatHHI(postMergerHHITotal - preMergerHHITotal)}
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );
  const tableTotals: JSX.Element | JSX.Element[] =
    Object.keys(totals).length > 0 ? (
      <>
        <TableRow id="totals" key="totals">
          <TableCell
            style={headerStyle(primaryHeaderColor, "left")}
          ></TableCell>

          <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
          <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
          <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
          <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
          <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
          <TableCell
            style={headerStyle(primaryHeaderColor, "center", "0", 0, true)}
          >
            {isBhc ? "Sub Totals" : "Totals"}
          </TableCell>
          <TableCell style={headerStyle(additionalHeaderColor, "right")}>
            {!withBhc
              ? noBHCTotals?.ASSET?.toLocaleString() || 0
              : totals?.ASSET?.toLocaleString() || 0}
          </TableCell>
          <TableCell style={headerStyle(additionalHeaderColor, "center")}>
            {!withBhc
              ? noBHCTotals?.count?.toLocaleString() || 0
              : (
                  totals.OFFICES - (noBHCTotals?.count || 0)
                )?.toLocaleString() || 0}
          </TableCell>
          <TableCell style={headerStyle(additionalHeaderColor, "right")}>
            {!withBhc
              ? noBHCTotals?.sum_DEPSUMBR?.toLocaleString() || 0
              : (
                  totals.DEPOSITS - (noBHCTotals?.sum_DEPSUMBR || 0)
                )?.toLocaleString() || 0}
          </TableCell>
          <TableCell style={headerStyle(additionalHeaderColor, "center")}>
            {!withBhc
              ? noBHCTotals?.SHARE?.toFixed(2) || 0
              : (
                  (100 * (totals.DEPOSITS - (noBHCTotals?.sum_DEPSUMBR || 0))) /
                  totals.DEPOSITS
                ).toFixed(2)}
            %
          </TableCell>
          <TableCell style={headerStyle(primaryHeaderColor, "center")}>
            {isBhc
              ? !withBhc
                ? formatHHI(totalsNoBHC)
                : formatHHI(totalsBHC)
              : ""}
          </TableCell>
        </TableRow>
        <TableRow id="totalsHHI" key="totalsHHI">
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          {!reportFinalized && (
            <TableCell
              colSpan={4}
              style={{
                fontWeight: "bold",
                textAlign: "right",
                alignItems: "right",
                flex: "nowrap",
              }}
            >
              {hhiTooltip("HHI Calculation")}
            </TableCell>
          )}
        </TableRow>
      </>
    ) : (
      [<tr key="no-results"></tr>]
    );

  const selectedMarkets: string[][] = [];

  if (
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
  ) {
    for (let i = 0; i < appState.filters.stateNames.length; i++) {
      const market = [
        getStateName(selectedStatesAlpha, appState.filters.stateNames[i]),
        appState.ui.marketSelection[i]?.counties?.join(", "),
        appState.ui.marketSelection[i]?.cities?.join(", "),
        appState.ui.marketSelection[i]?.zips?.join(", "),
      ];
      selectedMarkets.push(market);
    }
  }

  const bankOrgCount: any[] = [
    totals.OFFICES - noBHCTotals?.count,
    noBHCTotals?.count,
    totals.OFFICES,
  ];
  const bankOrgTotalDep: any[] = [
    totals.DEPOSITS - noBHCTotals?.sum_DEPSUMBR,
    noBHCTotals?.sum_DEPSUMBR,
    totals.DEPOSITS,
  ];
  const bankOrgMarketShare = [
    totals.DEPOSITS && noBHCTotals?.sum_DEPSUMBR !== undefined
      ? `${(
          (100 * (Number(totals.DEPOSITS) - noBHCTotals?.sum_DEPSUMBR)) /
          totals.DEPOSITS
        ).toFixed(2)}%`
      : "0.00%",
    totals.DEPOSITS && noBHCTotals?.sum_DEPSUMBR
      ? `${((100 * Number(noBHCTotals.sum_DEPSUMBR)) / totals.DEPOSITS).toFixed(
          2,
        )}%`
      : "0.00%",
    "100.00%",
  ];

  for (let i = 0; i < scopeTotalsArray.length; i++) {
    const bankOrg = [
      scopeTotalsArray[i],
      bankOrgCount[i]?.toLocaleString(),
      bankOrgTotalDep[i]?.toLocaleString(),
      bankOrgMarketShare[i],
      totalsHHI[i],
    ];
    totalBankOrg.push(bankOrg);
  }

  const selectedMarket =
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
      ? [
          ["Selected Market"],
          ["State", "Counties", "Cities", "ZIP Codes"],
          ...selectedMarkets,
          [],
        ]
      : [];
  const hhiCalculations = reportFinalized
    ? [
        [["HHI Description"], ["Value"]],
        [
          ["Pre-Merger HHI"],
          [preMergerHHITotal ? formatHHI(preMergerHHITotal) : ""],
        ],
        [
          ["Post-Merger HHI"],
          [postMergerHHITotal ? formatHHI(postMergerHHITotal) : ""],
        ],
        [
          ["Change in HHI Points"],
          [
            postMergerHHITotal && preMergerHHITotal
              ? formatHHI(postMergerHHITotal - preMergerHHITotal)
              : "",
          ],
        ],
      ]
    : [[["HHI Calculation"], [Number(totals.HHI)?.toLocaleString()]]];

  const combinedData = (noMarket?: boolean) => {
    const header = noMarket
      ? [
          [],
          [
            `Holding Companies with Subsidiaries in Market: ${institutionsCount?.toLocaleString()}`,
          ],
        ]
      : selectedMarket;

    if (!noMarket) {
      const additionalRow = reportFinalized ? [""] : [];
      tableDownloadData.push([
        ...additionalRow,
        "",
        "",
        "",
        "",
        "",
        "Totals",
        (totals?.ASSET || 0)?.toLocaleString() || 0,
        Number(totals?.OFFICES).toLocaleString(),
        Number(totals?.DEPOSITS).toLocaleString(),
        `${(
          (100 * (Number(totals.DEPOSITS) - noBHCTotals?.sum_DEPSUMBR)) /
          totals.DEPOSITS
        ).toFixed(2)}%`,
      ]);

      noBHCData.length === 0 &&
        hhiCalculations.forEach((hhi: string[][]) => {
          tableDownloadData.push([
            ...additionalRow,
            [],
            [],
            [],
            [],
            [],
            [],
            [],
            [],
            [],
            ...hhi,
          ]);
        });
    }
    return [...header, tableDownloadHeaders, ...tableDownloadData];
  };

  const combinedDataNoBhc = (noMarket?: boolean) => {
    const header = noMarket
      ? [
          [],
          [
            `Institutions without Holding Companies in Market: ${noBHCData.length?.toLocaleString()}`,
          ],
        ]
      : selectedMarket;
    if (!noMarket) {
      const additionalRow = reportFinalized ? [""] : [];
      tableDownloadDataNoBHCs.push([
        ...additionalRow,
        "",
        "",
        "",
        "",
        "",
        "Totals",
        Number(noBHCTotals?.ASSET || 0)?.toLocaleString() || 0,
        noBHCTotals.count?.toLocaleString(),
        noBHCTotals.sum_DEPSUMBR?.toLocaleString(),
        `${((100 * noBHCTotals.sum_DEPSUMBR) / totals.DEPOSITS).toFixed(2)}%`,
      ]);

      noBHCData.length > 0 &&
        hhiCalculations.forEach((hhi: string[][]) => {
          const hhiValue = reportFinalized
            ? hhi
            : [["HHI Calculation"], Number(hhiNoBHC)?.toLocaleString()];
          tableDownloadDataNoBHCs.push([
            ...additionalRow,
            [],
            [],
            [],
            [],
            [],
            [],
            [],
            [],
            [],
            ...hhiValue,
          ]);
        });
    }
    return [...header, tableDownloadHeadersNoBhc, ...tableDownloadDataNoBHCs];
  };

  const combinedDataTotal = (noMarket?: boolean) => {
    const header = noMarket
      ? [[`Total Bank Organizations in Market: ${totalBankOrgs}`]]
      : selectedMarket;
    return [...header, tableDownloadHeadersTotalOrg, ...totalBankOrg];
  };

  const downloadFileTitle = () => {
    let title = "";
    if (reportFinalized) {
      if (appState.filters.msa) {
        title = `ProForma_Final_${appState.filters.msa}`;
      } else if (appState.filters.zipbr) {
        title = `ProForma_Final_ZIP_${appState.filters.zipbr}`;
      } else {
        title = `ProForma_Final_Market`;
      }
    }
    if (!reportFinalized) {
      if (appState.filters.msa) {
        title = `ProForma_${appState.filters.msa}`;
      } else if (appState.filters.zipbr) {
        title = `ProForma_ZIP_${appState.filters.zipbr}`;
      } else {
        title = `ProForma_Market`;
      }
    }
    return title;
  };

  const isChecked = certsToMerge.length > 0 && certsToMerge.includes(cert);

  const modalSubtitle = (certAcquiring[0] &&
    cert &&
    !certAcquiring.includes(cert) &&
    !rowIsBHC && (
      <>
        <Typography variant="h2">
          Branch Offices Available For Acquisition
        </Typography>
        <Typography>
          This interface allows you to manually pick individual branch offices
          to merge into your acquiring institution (partial merger.)
        </Typography>
      </>
    )) || (
    <>
      <Typography variant="h2">
        Acquiring Institution Branch Offices.
      </Typography>
      <Typography>
        This interface allows you to view the individual branch offices of your
        selected acquiring institution.
      </Typography>
    </>
  );

  const branchOfficeModal = (
    <ModalDialog
      subtitle={modalSubtitle}
      title={name}
      display={openModal !== ""}
      setDisplay={() => setOpenModal("")}
      maxWidth="lg"
      submitText="Continue"
      partialMergerModal={
        certAcquiring[0] && cert && !certAcquiring.includes(cert) && !rowIsBHC
      }
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          ui: {
            ...appState.ui,
            institutionType: rowIsBHC ? "bhcs" : "banks",
            partialMerger:
              certAcquiring[0] &&
              cert &&
              !certAcquiring.includes(cert) &&
              !rowIsBHC
                ? [{ isChecked: isChecked }, ...appState.ui.partialMerger]
                : [],
          },
          filters: {
            ...appState.filters,
            cert: rowIsBHC ? "" : openModal,
            bhcRSSD: rowIsBHC ? openModal : "",
          },
        }}
      />
    </ModalDialog>
  );
  const importantInfoLink = isBhc ? (
    <Button
      className={classes.buttonLink}
      onClick={() => window.scrollTo(0, document.body.scrollHeight)}
    >
      Click for important information about this data.
    </Button>
  ) : (
    <></>
  );

  const footer = (
    <Typography className={classes.footer}>
      <b>Note about this data for BHCs or Bank Holding Companies:</b>
      <br />
      This search will return results for top regulatory bank holding companies
      and their FDIC-insured subsidiaries. The regulatory top holder is assigned
      by the Federal Reserve Board based on ownership and control percentages.
      Thrift bank holding company information is not available. Official bank
      holding company information can be found by visiting the website of the
      Federal Reserve System - National Information Center. Total reflects the
      sum of all MSAs.
    </Typography>
  );
  const fileNameBhc = appState.ui.institutionType === "bhcs" ? "_with_BHC" : "";
  const proFormaTitle = isBhc
    ? "Pro-Forma Results"
    : `Pro-Forma - ${tableData.length?.toLocaleString()} Matching Results`;

  const apiDataNoBhc: apiDownloadProps = {
    appState,
    fileName: `${fileNameFormat(downloadFileTitle()).replace(
      /__/g,
      "_",
    )}_without_BHC.csv`,
    apiPath: "sod",
    text: "Download Results Data - Institutions without BHC",
    data: combinedDataNoBhc() as string[][],
  };

  const apiDataTotalOrg: apiDownloadProps = {
    appState,
    fileName: `${fileNameFormat(downloadFileTitle()).replace(
      /__/g,
      "_",
    )}_total_org.csv`,
    apiPath: "sod",
    text: "Download Total Bank Organizations",
    data: combinedDataTotal() as string[][],
  };

  const apiData: apiDownloadProps = {
    appState,
    fileName: `${fileNameFormat(downloadFileTitle()).replace(
      /__/g,
      "_",
    )}${fileNameBhc}.csv`,
    apiPath: "sod",
    text:
      appState.ui.institutionType === "bhcs"
        ? "Download Results Data -  BHCs with Subsidiaries"
        : "Download Results Data - Formatted",
    data: combinedData() as string[][],
  };

  const apiDataAll: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(downloadFileTitle()).replace(
        /__/g,
        "_",
      )}${fileNameBhc}_ALL.csv`,
      apiPath: "sod",
      text: "Download All Results Data",
      data: [
        ...selectedMarket,
        ...combinedDataTotal(true),
        ...combinedData(true),
        ...(noBHCData.length > 0 ? combinedDataNoBhc(true) : []),
      ],
    },
  ];
  if (appState.ui.institutionType === "bhcs") {
    apiDataAll.push(apiDataTotalOrg);
    apiDataAll.push(apiData);
    noBHCData.length > 0 && apiDataAll.push(apiDataNoBhc);
  }

  //sets initial report period
  React.useEffect(() => {
    if (!reportPeriod) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&offset=0&sort_by=RISDATE&sort_order=DESC&fields=RISDATE`,
        ).then((response) => {
          if (isMounted) {
            const date = response.data[0].RISDATE;
            const latestRepSOD = `${appState.filters.lastYear}0630`;
            setReportPeriod(
              Number(date) < Number(latestRepSOD) ? date : latestRepSOD,
            );
            setPeriod(
              Number(date) < Number(latestRepSOD) ? date : latestRepSOD,
            );
          }
        });
      })();
    }
  }, [appState.filters.lastYear, reportPeriod]);

  //Beginning of States Data
  React.useEffect(() => {
    if (
      appState.filters.stateNames[0] === "ALL" &&
      JSON.stringify(state) !== JSON.stringify(previousState)
    ) {
      setPreviousState(state);
      softResetReport();
      setLoading(true);
      let isMounted = true;
      const filters = isBhc
        ? "&sort_by=BHC_KEY&sort_order=asc"
        : " AND CERT_KEY:>-1&sort_by=CERT_KEY&sort_order=asc";
      const requests = [
        `${ApiBaseUrl()}/sod_agg?limit=10000&offset=0&filters=YEAR:${
          appState.filters.lastYear
        }${filters}`,
        `${ApiBaseUrl()}/sod_agg?limit=10000&offset=10000&filters=YEAR:${
          appState.filters.lastYear
        }${filters}`,
      ];

      (async () => {
        await Promise.all(requests.map((url: string) => fetchData(url)))
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response
                .map((res: KeyValue) => res.data)
                .flat();

              if (isBhc) {
                const newData: KeyValue[] = [];
                let offices = 0;
                let deposits = 0;
                combinedResponse.forEach((val) => {
                  if (!val.CERT) {
                    offices += val.count;
                    deposits += val.sum_DEPSUMBR;
                  }
                });

                const transformedData = combinedResponse.map((data) => {
                  const share = (data.sum_DEPSUMBR / deposits) * 100;
                  return { ...data, SHARE: share, SQUARED: share * share };
                });

                const withoutBHC = transformedData.filter(
                  (data) => data.RSSDHCR === 0 && data?.CERT,
                );
                const withBHC = transformedData.filter(
                  (data) => data.RSSDHCR !== 0,
                );

                setNoBHCData(withoutBHC);

                withBHC.forEach((data) => {
                  if (!data.CERT) {
                    newData.push({ ...data, subData: [] });
                  } else {
                    const record = newData.find(
                      (bhc) => bhc.RSSDHCR === data.RSSDHCR,
                    );
                    if (record) {
                      record.subData.push(data);
                    }
                  }
                });

                const bhcs = withBHC.filter((data) => data.CERT === null);

                const certs = withBHC
                  .filter((data) => data.CERT !== null)
                  .map((res) => res.CERT);
                const rssdhcr = bhcs
                  .filter((data) => data.RSSDHCR !== 0)
                  .map((res) => res.RSSDHCR);
                setRssdhcrArray(rssdhcr);
                setCertArray(certs);

                setRecordCount(transformedData.length);
                setTotals({
                  OFFICES: offices,
                  DEPOSITS: deposits,
                  SHARE: "100.00",
                  HHI: sumValueArrayObj(bhcs, "SQUARED"),
                });

                setTableData(newData);
                setLoading(false);
                setGetFinancials(true);
              } else {
                let offices = 0;
                let deposits = 0;
                combinedResponse.forEach((val) => {
                  offices += val.count;
                  deposits += val.sum_DEPSUMBR;
                });
                const transformedData = combinedResponse.map((data) => {
                  const share = (data.sum_DEPSUMBR / deposits) * 100;
                  return { ...data, SHARE: share, SQUARED: share * share };
                });
                const certs = combinedResponse.map((res) => res.CERT);
                setCertArray(certs);
                setTotals({
                  OFFICES: offices,
                  DEPOSITS: deposits,
                  SHARE: "100.00",
                  HHI: sumValueArrayObj(transformedData, "SQUARED"),
                });

                setTableData(transformedData);
                setRecordCount(transformedData.length);
                setGetFinancials(true);
                const resData = response
                  .map((res) => res.data)
                  .flat()
                  .map((data) => data[key]);
                setCertArray(resData);
              }

              setLoading(false);
              const noBhcData: KeyValue[] = response
                .map((res) => res.data)
                .flat()
                .filter((obj: KeyValue) => {
                  return obj.RSSDHCR === 0;
                });
              noBhcData.length && isBhc && setGetNoBhcData(true);
            }
          })
          .finally(() => {
            if (isBhc) {
              setGetSubFinancials(true);
            } else setLoading(false);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.filters.stateNames,
    isBhc,
    key,
    previousState,
    softResetReport,
    state,
  ]);
  React.useEffect(() => {
    if (getAllStatesAggs) {
      setGetAllStatesAggs(false);
      let isMounted = true;
      (async () => {
        const splitCerts = chunkArray(
          key === "CERT" ? certArray : rssdhcrArray,
          500,
        );
        const certFetches = splitCerts.map((certArrays) => {
          const allCerts = `AND ${key}:(${certArrays.join(" OR ")})`;

          return fetchData(
            `${ApiBaseUrl()}/sod`,
            apiPost({
              agg_limit: 10000,
              agg_by: key,
              agg_sum_fields: "DEPSUMBR",
              filters: `YEAR:${appState.filters.lastYear} ${allCerts}`,
            }),
          );
        });
        await Promise.all(certFetches)
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response.map((res) => res.data).flat();
              combinedResponse.length === 0 && setLoading(false);
              const combinedTotals: KeyValue = response
                .map((res) => res.totals)
                .flat();
              let offices = 0;
              let deposits = 0;

              Object.keys(combinedTotals).forEach((val: string) => {
                offices = combinedTotals[val].count + offices;
                deposits = combinedTotals[val].sum_DEPSUMBR + deposits;
              });
              setSelectedMarketData(combinedResponse);
              setTotals({
                OFFICES: offices,
                DEPOSITS: deposits,
                SHARE: "100.00",
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetInstData(true);
          });
      })();
    }
  }, [
    certArray,
    rssdhcrArray,
    appState.filters.lastYear,
    getAllStatesAggs,
    isBhc,
    key,
  ]);

  React.useEffect(() => {
    if (
      appState.filters.stateNames[0] !== "ALL" &&
      (Object.keys(appState.ui.marketSelection?.[0]).length > 0 ||
        appState.filters.stateNames[0]) &&
      JSON.stringify(state) !== JSON.stringify(previousState) &&
      appState.filters.stateNames[0] !== "ALL"
    ) {
      softResetReport();
      setLoading(true);
      setPreviousState(state);
      let isMounted = true;
      const inMarketState = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          const marketSelecion = appState.ui.marketSelection[index];
          const zips = chunkArray(marketSelecion.zips, 500);
          return zips.map((arr) =>
            generateBranchOfficeDepositsApiUrl(
              appState,
              {
                by: key,
                limit: 10000,
                sum: ["DEPSUMBR"],
              },
              ...[state, { ...marketSelecion, zips: arr }, undefined],
            ),
          );
        });

      (async () => {
        await Promise.all(
          inMarketState
            .flat()
            .map((url: string) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response
                .map((res: KeyValue) => res.data)
                .flat();
              const noBhcData: KeyValue[] = combinedResponse.filter(
                (obj: KeyValue) => {
                  return obj.RSSDHCR === 0;
                },
              );
              noBhcData.length && isBhc && setGetNoBhcData(true);
              const combineData = (data: KeyValue[]): KeyValue[] => {
                const combinedData: { [key: number]: KeyValue } = {};

                data.forEach((item) => {
                  if (combinedData[item[key]]) {
                    combinedData[item[key]].count += item.count;
                    combinedData[item[key]].sum_DEPSUMBR += item.sum_DEPSUMBR;
                  } else {
                    combinedData[item[key]] = { ...item };
                  }
                });

                return Object.values(combinedData).map((value) => {
                  return {
                    [key]: value[key],
                    count: value.count,
                    sum_DEPSUMBR: value.sum_DEPSUMBR,
                  };
                });
              };
              const combinedData = combineData(combinedResponse);
              const ids: number[] =
                appState.filters.stateNames[0] === "ALL"
                  ? [0]
                  : combinedData.map((res: KeyNumberValue) => res[key]);
              const filteredIds = Array.from(new Set(ids));
              if (filteredIds.length) {
                key === "CERT"
                  ? setCertArray(filteredIds)
                  : setRssdhcrArray(filteredIds);
              }
              setSelectedMarketData(combinedData);
              let count = 0;
              let sumDepsum = 0;
              response.forEach((item: KeyValue) => {
                count = count + item.totals.count;
                sumDepsum = sumDepsum + item.totals.sum_DEPSUMBR;
              });
              setTotals({
                OFFICES: count,
                DEPOSITS: sumDepsum,
                SHARE: "100.00",
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetInstData(true);
          });
      })();
    }
  }, [appState, isBhc, key, previousState, softResetReport, state]);

  React.useEffect(() => {
    if (
      isBhc &&
      getNoBhcData &&
      appState.filters.stateNames[0] &&
      appState.filters.lastYear
    ) {
      setGetNoBhcData(false);
      let isMounted = true;
      const inMarketState = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          if (state?.toUpperCase() === "ALL") {
            return generateBranchOfficeDepositsApiUrl(
              appState,
              {
                by: "CERT",
                limit: 10000,
                sum: ["DEPSUMBR"],
              },
              state,
              undefined,
              undefined,
              "RSSDHCR:0",
            );
          } else {
            const marketSelecion = appState.ui.marketSelection[index];
            const zips = chunkArray(marketSelecion.zips, 500);
            return zips.map((arr) =>
              generateBranchOfficeDepositsApiUrl(
                appState,
                {
                  by: "CERT",
                  limit: 10000,
                  sum: ["DEPSUMBR"],
                },
                state,
                { ...marketSelecion, zips: arr },
                undefined,
                "RSSDHCR:0",
              ),
            );
          }
        });
      (async () => {
        await Promise.all(
          inMarketState
            .flat()
            .map((url: string) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            if (isMounted) {
              const data: KeyValue[] = response.map((res) => res.data).flat();
              setNoBHCData(data);

              const certValues = data.map((item: KeyValue) => item.CERT);
              setCertArrayNoBHC(certValues);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
            setGetNoBHCInstData(true);
            setGetNoBHCCombinedData(true);
          });
      })();
    }
  }, [
    appState,
    initialLoad,
    getNoBhcData,
    noBHCData,
    previousState,
    state,
    isBhc,
    key,
  ]);

  //begining of MSA/ZIPBR Data
  React.useEffect(() => {
    if (
      (appState.filters.msa || appState.filters.zipbr) &&
      appState.filters.lastYear &&
      (JSON.stringify(state) !== JSON.stringify(previousState) || initialLoad)
    ) {
      softResetReport();
      setInitialLoad(false);
      setPreviousState(state);
      setNoBHCData([]);
      setCertArrayNoBHC([]);
      if (isBhc) {
        let isMounted = true;
        const filterKey = appState.filters.msa
          ? `MSANAMB:"${appState.filters.msa}"`
          : `ZIPBR:${appState.filters.zipbr}`;
        (async () => {
          await Promise.resolve(
            fetchData(
              `${ApiBaseUrl()}/sod?filters=${filterKey} AND YEAR:${
                appState.filters.lastYear
              } AND RSSDHCR:0
              &agg_by=CERT&get_unique=CERT&agg_sum_fields=DEPSUMBR`,
            ),
          )
            .then((response) => {
              if (isMounted) {
                const data: KeyValue[] = response.data;
                setNoBHCData(data);
                const certValues = data.map((item: KeyValue) => item.CERT);
                setCertArrayNoBHC(certValues);
              }
            })
            .catch((error) => {
              console.error(`${error.name} : ${error.message}`);
            })
            .finally(() => {
              isMounted = false;
              setGetNoBHCCombinedData(true);
            });
        })();
      }
    }
  }, [
    appState.filters.lastYear,
    appState.filters.msa,
    appState.filters.zipbr,
    initialLoad,
    noBHCData,
    previousState,
    state,
    softResetReport,
    isBhc,
  ]);
  React.useEffect(() => {
    if (
      appState.filters.lastYear &&
      JSON.stringify(state) !== JSON.stringify(previousState) &&
      (appState.filters.msa !== "" || appState.filters.zipbr !== "")
    ) {
      setPreviousState(state);
      const msaZipFilter = appState.filters.msa
        ? ` AND MSANAMB:"${appState.filters.msa}"`
        : ` AND ZIPBR:"${appState.filters.zipbr}"`;
      setLoading(true);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${
            appState.filters.lastYear
          }${msaZipFilter}&agg_by=${key}&agg_sum_fields=DEPSUMBR&sort_by=DEPSUMBR&sort_order=DESC&agg_limit=${10000}&gitoffset=${0}`,
        )
          .then((response) => {
            if (isMounted) {
              const ids = response.data.map((e: KeyNumberValue) => e[key]);
              key === "CERT" ? setCertArray(ids) : setRssdhcrArray(ids);
              if (!ids.length) {
                setLoading(false);
              }
              setSelectedMarketData(response.data);
              const noBhcData: KeyValue[] = response.data.filter(
                (obj: KeyValue) => {
                  return obj.RSSDHCR === 0;
                },
              );
              const noBhcDeposit =
                sumValueArrayObj(noBhcData, "sum_DEPSUMBR") || 0;

              setTotals({
                OFFICES: response.totals.count,
                DEPOSITS: response.totals.sum_DEPSUMBR,
                SHARE: noBhcDeposit
                  ? (
                      ((response.totals.sum_DEPSUMBR - noBhcDeposit) /
                        response.totals.sum_DEPSUMBR) *
                      100
                    ).toFixed(2)
                  : "100.00",
              });
              noBhcDeposit && setGetNoBHCInstData(true);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            !isBhc && setLoading(false);
            setGetInstData(true);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.filters.msa,
    appState.filters.zipbr,
    key,
    previousState,
    state,
    isBhc,
    noBHCData,
  ]);

  //Institution Data for All
  React.useEffect(() => {
    const keyArray = key === "CERT" ? certArray : rssdhcrArray;
    if (getInstData && keyArray.length > 0) {
      setGetInstData(false);

      setDisplayError(false);
      let isMounted = true;
      (async () => {
        const splitCerts = chunkArray(keyArray, 500);
        const certFetches = splitCerts.map((certArrays) => {
          const allCerts = ` AND ${key}:(${certArrays.join(" OR ")})`;

          const fields = isBhc
            ? "RSSDHCR,NAMEHCR,STALPHCR"
            : "BKCLASS,NAMEFULL,CHARTER,CERT,STALP";

          const bhcFilter = isBhc ? " AND RSSDHCR:>0" : "";

          return fetchData(
            `${ApiBaseUrl()}/sod`,
            apiPost({
              limit: 10000,
              offset: 0,
              fields: fields,
              filters: `YEAR:${appState.filters.lastYear}${allCerts}${bhcFilter}`,
              get_unique: key,
            }),
          );
        });
        await Promise.all(certFetches)
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response.map((res) => res.data).flat();
              const combinedMarketData = combinedResponse.map(
                (data: KeyValue) => {
                  const matchingMarketData = selectedMarketData.find(
                    (marketData: KeyValue) => marketData[key] === data[key],
                  );
                  const combinedRes = { ...data, ...matchingMarketData };
                  const marketShare =
                    (combinedRes.sum_DEPSUMBR / totals.DEPOSITS) * 100;

                  const squared = Number(
                    (marketShare * marketShare).toFixed(4),
                  );
                  const formattedRes = isBhc
                    ? {
                        RSSDHCR: combinedRes.RSSDHCR,
                        NAMEFULL: combinedRes.NAMEHCR,
                        STALP: combinedRes.STALPHCR,

                        count: combinedRes.count,
                        sum_DEPSUMBR: combinedRes.sum_DEPSUMBR,
                        SHARE: marketShare,
                        SQUARED: squared,
                        ASSET: 0,
                      }
                    : {
                        CERT: combinedRes.CERT,
                        NAMEFULL: combinedRes.NAMEFULL,
                        STALP: combinedRes.STALP,
                        BKCLASS: combinedRes.BKCLASS,
                        CHARTER: combinedRes.CHARTER,
                        count: combinedRes.count,
                        sum_DEPSUMBR: combinedRes.sum_DEPSUMBR,
                        SHARE: marketShare,
                        SQUARED: squared,
                        ASSET: 0,
                      };
                  return formattedRes;
                },
              );

              setRecordCount(response[0].totals.count);

              setTableData(combinedMarketData);
              setTotals({
                ...totals,
                HHI: sumValueArrayObj(combinedMarketData, "SQUARED"),
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetFinancials(true);
          });
      })();
    } else if (keyArray.length === 0 && getInstData) {
      setDisplayError(true);
    }
  }, [
    certArray,
    rssdhcrArray,
    appState.filters.lastYear,
    getInstData,
    isBhc,
    key,
    selectedMarketData,
    totals,
    noBHCData.length,
    totals.DEPOSITS,
  ]);

  //Financial Data for All
  React.useEffect(() => {
    if (getFinancials || getSubFinancials) {
      !onlyFinancials && setGetSubFinancials(false);
      if (onlyFinancials && !getSubFinancials) {
        setGetFinancials(false);
        setGetSubFinancials(true);
      } else if (onlyFinancials && getSubFinancials) {
        setOnlyFinancials(false);
        setGetSubFinancials(false);
      }
      setGetFinancials(false);
      let isMounted = true;
      isBhc && setLoading(true);
      (async () => {
        const financialKey = getSubFinancials ? "CERT" : key;
        const noBhcCerts = noBHCDataToMerge.length
          ? noBHCDataToMerge.map((data) => data.CERT)
          : [];
        const splitCerts = chunkArray(
          [
            ...(key === "CERT" || getSubFinancials ? certArray : rssdhcrArray),
            ...noBhcCerts,
          ],
          500,
        );
        const certFetches = splitCerts.map((certArrays) => {
          const allCerts = ` AND ${financialKey}:(${certArrays.join(" OR ")})`;
          return fetchData(
            `${ApiBaseUrl()}/financials`,
            apiPost({
              agg_limit: 10000,
              agg_by: financialKey,
              agg_sum_fields: "ASSET",
              filters: `!BKCLASS: (NS OR NC) AND REPDTE:${reportPeriod}${allCerts}`,
            }),
          );
        });

        await Promise.all(certFetches)
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response.map((res) => res.data).flat();
              const noBhcAcq = noBHCData
                .map((data) => data.NEWBANK)
                .some((val) => val);
              let noBhcAcqData: KeyValue[] = noBhcAcq
                ? noBHCData.find((data) => data.NEWBANK)?.old || []
                : [];
              if (noBhcAcqData.length) {
                noBhcAcqData = noBhcAcqData.filter((data) => data.RSSDHCR);
              }
              [...tableData, ...noBhcAcqData].forEach((item) => {
                if (getSubFinancials && item.subData) {
                  item.subData.forEach((subItem: KeyValue) => {
                    const matchedResponse = combinedResponse.find(
                      (response) =>
                        response[financialKey]?.toString() ===
                        subItem[financialKey]?.toString(),
                    );
                    if (matchedResponse) {
                      subItem.ASSET = matchedResponse.sum_ASSET;
                    } else {
                      subItem.ASSET = 0;
                    }
                  });
                } else {
                  const matchedResponse = combinedResponse.find(
                    (response) =>
                      response[key]?.toString() === item[key]?.toString(),
                  );
                  if (item.old) {
                    item.old.forEach((oldItem: KeyValue) => {
                      if (getSubFinancials && oldItem.subData) {
                        oldItem.subData.forEach((subItem: KeyValue) => {
                          const matchedResponse = combinedResponse.find(
                            (response) =>
                              response[financialKey]?.toString() ===
                              subItem[financialKey]?.toString(),
                          );
                          if (matchedResponse) {
                            subItem.ASSET = matchedResponse.sum_ASSET;
                            if (
                              oldItem.NAMEFULL === "Institutions without BHC" &&
                              noBhcCerts.includes(subItem.CERT)
                            ) {
                              oldItem.ASSET += matchedResponse.sum_ASSET;
                            }
                          } else {
                            subItem.ASSET = 0;
                          }
                        });
                      } else {
                        const matchedResponse = combinedResponse.find(
                          (response) =>
                            response[financialKey]?.toString() ===
                            oldItem[financialKey]?.toString(),
                        );
                        if (matchedResponse) {
                          oldItem.ASSET = matchedResponse.sum_ASSET;
                        } else {
                          oldItem.ASSET = 0;
                        }
                      }
                    });
                  }
                  if (item.NEWBANK) {
                    let val = 0;
                    item.old.forEach((oldData: KeyValue) => {
                      val += oldData.ASSET;
                    });
                    item.ASSET = val;
                  } else if (matchedResponse) {
                    item.ASSET = matchedResponse.sum_ASSET;
                  } else {
                    item.ASSET = 0;
                  }
                }
              });
              const newData = !reportFinalized
                ? sortObjValue(tableData, "sum_DEPSUMBR", true)
                : tableData;
              setTableData(newData);
              let ASSET = 0;
              newData.forEach((val) => {
                ASSET += val.ASSET;
              });
              setTotals({ ...totals, ASSET: ASSET });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setLoading(false);
            isMounted = false;
            !isBhc && setLoading(false);
            !onlyFinancials &&
              isBhc &&
              !getSubFinancials &&
              setGetSubData(true);
            isBhc && getSubFinancials && setLoading(false);
          });
      })();
    }
  }, [
    certArray,
    rssdhcrArray,
    certAcquiring,
    certsToMerge,
    getFinancials,
    getSubFinancials,
    isBhc,
    key,
    onlyFinancials,
    period,
    reportFinalized,
    reportPeriod,
    tableData,
    totals,
    noBHCData,
    certArrayNoBHC,
    noBHCDataToMerge,
  ]);

  //Sub Data for States
  React.useEffect(() => {
    if (
      getSubData &&
      appState.filters.stateNames[0] &&
      appState.filters.stateNames[0] !== "ALL"
    ) {
      setLoading(true);
      setGetSubData(false);
      const splitBHCs = chunkArray(rssdhcrArray, 300);
      const allInst =
        appState.filters.stateNames[0] &&
        appState.filters.stateNames[0] === "ALL";

      const selectedMarket = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          const newMarket = splitBHCs.map((bhcs) => {
            const additionalFilters = `RSSDHCR:(${bhcs.join(
              " OR ",
            )}) AND !RSSDHCR:0`;
            const marketSelecion = appState.ui.marketSelection[index];
            const zips = chunkArray(marketSelecion.zips, 500);
            return zips.map((arr) =>
              generateBranchOfficeDepositsApiUrl(
                appState,
                {
                  by: "CERT",
                  limit: 10000,
                  sum: ["DEPSUMBR"],
                },

                state,
                { ...marketSelecion, zips: arr },
                undefined,
                additionalFilters,
              ),
            );
          });
          return newMarket;
        });

      const allApiCalls = splitBHCs.map((data) => {
        const additionalFilters = `RSSDHCR:(${data.join(
          " OR ",
        )}) AND !RSSDHCR:0`;
        return apiPost({
          filters: `YEAR:${appState.filters.lastYear} AND ${additionalFilters}`,
          agg_limit: 1000,
          agg_by: "CERT",
          agg_sum_fields: "DEPSUMBR",
        });
      });
      const apiCalls = allInst ? allApiCalls : selectedMarket.flat(2);

      let isMounted = true;
      (async () => {
        await Promise.all(
          apiCalls.map((post: any) => fetchData(`${ApiBaseUrl()}/sod`, post)),
        )
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response
                .map((res: KeyValue) => res.data)
                .flat();
              const combinedObj: KeyValue = {};
              combinedResponse.forEach((res: KeyValue) => {
                if (combinedObj[res.CERT]) {
                  combinedObj[res.CERT].count += res.count;
                  combinedObj[res.CERT].sum_DEPSUMBR += res.sum_DEPSUMBR;
                } else {
                  combinedObj[res.CERT] = {
                    count: res.count,
                    sum_DEPSUMBR: res.sum_DEPSUMBR,
                  };
                }
              });
              const formattedData = Object.keys(combinedObj).map((key) => {
                return {
                  CERT: Number(key),
                  sum_DEPSUMBR: combinedObj[key].sum_DEPSUMBR,
                  count: combinedObj[key].count,
                };
              });

              setSubDataCerts(formattedData);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetSubInstData(true);
          });
      })();
    }
  }, [appState, getSubData, rssdhcrArray]);

  //Sub Data for msa/zip
  React.useEffect(() => {
    if (getSubData && (appState.filters.msa || appState.filters.zipbr)) {
      setLoading(true);
      setGetSubData(false);
      const currentBHCs = tableData.map((data) => data.RSSDHCR).join(" OR ");
      const msaZipFilter = appState.filters.msa
        ? ` AND MSANAMB:"${appState.filters.msa}"`
        : ` AND ZIPBR:"${appState.filters.zipbr}"`;
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          apiPost({
            filters: `YEAR:${appState.filters.lastYear} AND RSSDHCR:(${currentBHCs})${msaZipFilter}`,
            agg_limit: 10000,
            agg_by: "CERT",
            agg_sum_fields: "DEPSUMBR",
          }),
        )
          .then((response) => {
            if (isMounted) {
              setSubDataCerts(
                response.data.map((data: KeyValue) => {
                  return {
                    CERT: data.CERT,
                    sum_DEPSUMBR: data.sum_DEPSUMBR,
                    count: data.count,
                  };
                }),
              );
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetSubInstData(true);
          });
      })();
    }
  }, [appState, getSubData, rssdhcrArray, isBhc, tableData]);

  React.useEffect(() => {
    if (getSubInstData && subDataCerts.length > 0) {
      setGetSubInstData(false);
      const certs = subDataCerts.map((data) => data.CERT);
      const splitCertData = chunkArray(certs, 500);
      const apiCalls = splitCertData.map((data) => {
        const additionalFilters = `CERT:(${data.join(" OR ")})`;
        return apiPost({
          filters: `YEAR:${appState.filters.lastYear} AND ${additionalFilters} AND BKMO:1`,
          limit: 500,
          fields: "NAMEFULL,RSSDHCR,STALP,BKCLASS,CHARTER,CERT",
        });
      });
      let isMounted = true;
      (async () => {
        await Promise.all(
          apiCalls.map((post: any) => fetchData(`${ApiBaseUrl()}/sod`, post)),
        )
          .then((response) => {
            if (isMounted) {
              let combinedResponse = response
                .map((res: KeyValue) => res.data)
                .flat();
              combinedResponse = combinedResponse.map((res) => {
                const match = subDataCerts.find(
                  (data) => data.CERT === res.CERT,
                );
                return { ...res, ...match };
              });
              const certs: number[] = [];

              const tableWithSubData = tableData.map((data: KeyValue) => {
                const matchingSubData: KeyValue[] = [];

                combinedResponse.forEach((subData: KeyValue) => {
                  const mkt = Number(
                    (subData.sum_DEPSUMBR / totals.DEPOSITS) * 100,
                  );

                  if (subData.RSSDHCR === data.RSSDHCR) {
                    if (certs.includes(subData.CERT)) {
                      matchingSubData.forEach((data) => {
                        if (data.CERT === subData.CERT) {
                          data.count += subData.count;
                          data.sum_DEPSUMBR += subData.sum_DEPSUMBR;
                          data.SHARE += mkt;
                          data.SQUARED = Number(
                            (data.SHARE * data.SHARE).toFixed(4),
                          );
                        }
                      });
                    } else {
                      certs.push(subData.CERT);

                      matchingSubData.push({
                        ...subData,
                        SHARE: mkt,
                        SQUARED: Number((mkt * mkt).toFixed(4)),
                      });
                    }
                  }
                });
                return {
                  ...data,
                  subData: sortObjValue(matchingSubData, "sum_DEPSUMBR", true),
                };
              });

              setTableData(tableWithSubData);
              setCertArray(certs);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetSubFinancials(true);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    getSubInstData,
    subDataCerts,
    tableData,
    totals.DEPOSITS,
  ]);

  React.useEffect(() => {
    if (
      getNoBHCInstData &&
      certArrayNoBHC.length > 0 &&
      noBHCData.length > 0 &&
      isBhc &&
      getNoBHCCombinedData &&
      totals.hasOwnProperty("DEPOSITS")
    ) {
      setGetNoBHCInstData(false);
      setGetNoBHCCombinedData(false);
      const splitCerts = chunkArray(certArrayNoBHC, 500);
      const certFetches = splitCerts.map((certArrays) => {
        const allCerts = ` AND CERT:(${certArrays.join(" OR ")})`;
        const fields = "BKCLASS,NAMEFULL,CHARTER,CERT,STALP";
        return fetchData(
          `${ApiBaseUrl()}/sod`,
          apiPost({
            limit: 10000,
            offset: 0,
            fields: fields,
            filters: `YEAR:${appState.filters.lastYear}${allCerts}`,
            get_unique: "CERT",
          }),
        );
      });
      let isMounted = true;
      (async () => {
        await Promise.all(certFetches)
          .then((response) => {
            if (isMounted) {
              const mergednoBHCData = Object.values(
                noBHCData.reduce((inst, item) => {
                  if (inst[item.CERT]) {
                    inst[item.CERT].sum_DEPSUMBR += item.sum_DEPSUMBR;
                    inst[item.CERT].count += item.count;
                  } else {
                    inst[item.CERT] = { ...item };
                  }
                  return inst;
                }, {}),
              );
              const combinedData: KeyValue[] = mergednoBHCData.map(
                (data: KeyValue) => {
                  const combinedData = response.map((res) => res.data).flat();

                  const match = combinedData.find(
                    (obj: KeyValue) => obj.CERT === data.CERT,
                  );
                  const marketShare =
                    (data.sum_DEPSUMBR / totals.DEPOSITS) * 100;
                  const squared = Number(
                    (marketShare * marketShare).toFixed(4),
                  );
                  return {
                    ...data,
                    NAMEFULL: match.NAMEFULL,
                    STALP: match.STALP,
                    BKCLASS: match.BKCLASS,
                    CHARTER: match.CHARTER,
                    SHARE: marketShare,
                    SQUARED: squared,
                    ASSET: 0,
                  };
                },
              );
              const newNoBHCData = sortObjValue(
                combinedData,
                "sum_DEPSUMBR",
                true,
              );
              setNoBHCData(newNoBHCData);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    certArrayNoBHC,
    appState.filters.lastYear,
    appState.filters.msa,
    getNoBHCInstData,
    key,
    noBHCData,
    totals,
    isBhc,
    getNoBHCCombinedData,
  ]);
  React.useEffect(() => {
    if (
      noBHCData.length > 0 &&
      noBHCData[0].hasOwnProperty("BKCLASS") &&
      certArrayNoBHC.length > 0 &&
      isBhc
    ) {
      let isMounted = true;
      (async () => {
        const splitCerts = chunkArray(certArrayNoBHC, 500);
        const certFetches = splitCerts.map((certArrays) => {
          const allCerts = ` AND CERT:(${certArrays.join(" OR ")})`;
          return fetchData(
            `${ApiBaseUrl()}/financials`,
            apiPost({
              agg_limit: 10000,
              agg_by: "CERT",
              agg_sum_fields: "ASSET",
              filters: `!BKCLASS: (NS OR NC) AND REPDTE:${reportPeriod}${allCerts}`,
            }),
          );
        });
        await Promise.all(certFetches)
          .then((response) => {
            if (isMounted) {
              const noBHCDataObj = noBHCData;

              const combinedResponse: KeyValue[] = response
                .map((res) => res.data)
                .flat();
              if (combinedResponse.length > 0) {
                combinedResponse.forEach((item: KeyValue) => {
                  let found = noBHCDataObj.find(
                    (noBHCItem) => noBHCItem.CERT === item.CERT,
                  );
                  if (found) {
                    found.ASSET = item.sum_ASSET;
                    if (found.old) {
                      found.old.forEach((oldItem: KeyValue) => {
                        const match = combinedResponse.find(
                          (res) => res.CERT === oldItem.CERT,
                        );
                        if (match) {
                          oldItem.ASSET = match.sum_ASSET;
                        }
                      });
                    }
                  }
                });
              }
              let hhiNoBHC = 0;
              noBHCDataObj.forEach((obj: KeyValue) => {
                hhiNoBHC += obj.SQUARED;
              });
              setHhiNoBHC(hhiNoBHC);
              setNoBHCData(noBHCDataObj);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    } else {
      setHhiNoBHC(0);
    }
  }, [certArrayNoBHC, isBhc, noBHCData, reportPeriod]);

  //Handles page jump and scroll after finalizing report
  React.useEffect(() => {
    if (
      pageJump ||
      (reportFinalized &&
        appState.pagination.resultLimit !== previousResultLimit)
    ) {
      setPageJump(false);
      setPreviousResultLimit(appState.pagination.resultLimit);
      const newPage = Math.floor(
        (noBHCAcqBHC ? noBHCData : tableData).findIndex(
          (data) => data.NEWBANK,
        ) / appState.pagination.resultLimit,
      );
      updatePage(newPage + 1);
      setScrollToRef(true);
    }
  }, [
    appState.pagination.resultLimit,
    noBHCAcqBHC,
    noBHCData,
    pageJump,
    previousResultLimit,
    reportFinalized,
    tableData,
    updatePage,
  ]);
  //handles scroll after it's initiated
  React.useEffect(() => {
    if (paginationRef.current && scrollToRef) {
      handleScroll(paginationRef.current);
      setScrollToRef(false);
    }
  }, [paginatedData, scrollToRef]);
  React.useEffect(() => {
    if (!isBhc) {
      setWithBhc(true);
    }
    if (
      !withBhc &&
      isBhc &&
      JSON.stringify(state) !== JSON.stringify(previousState) &&
      !noBHCAcqBHC
    ) {
      setWithBhc(true);
    }
  }, [
    appState.ui.institutionType,
    isBhc,
    noBHCAcqBHC,
    previousState,
    state,
    withBhc,
  ]);

  //Updates processed after selections are made for partial mergers
  React.useEffect(() => {
    if (appState.ui.closeModal && tableData.length) {
      const newValue: KeyValue =
        appState.ui.partialMerger[appState.ui.partialMerger.length - 1];
      const isPresent: boolean = partialMergerSelections.some(
        (obj: KeyValue) => obj.cert === newValue.cert,
      );

      if (newValue && !isPresent && newValue.count > 0) {
        if (certsToMerge.includes(newValue.cert)) {
          removeCert([newValue.cert]);
        }
        let matchingData: KeyValue = {};
        tableData.forEach((obj: KeyValue) => {
          if (isBhc && obj.RSSDHCR === newValue.rssdhcr) {
            matchingData = obj.subData.find(
              (data: KeyValue) => data.CERT === newValue.cert,
            );
          } else if (obj.CERT === newValue.cert) {
            matchingData = obj;
          }
        });
        if (matchingData?.count && matchingData.count === newValue.count) {
          isBhc
            ? addCert([newValue.cert, newValue.rssdhcr])
            : addCert([newValue.cert]);

          const updatedSelections = partialMergerSelections.filter(
            (obj: KeyValue) => obj.cert !== newValue.cert,
          );
          updateUi("partialMerger", updatedSelections);
          setPartialMergerSelections(updatedSelections);
        } else {
          matchingData &&
            setPmMatchingTableData((prevState) => [...prevState, matchingData]);
          const updatedSelections = [...partialMergerSelections, newValue];
          updateUi("partialMerger", updatedSelections);
          setPartialMergerSelections(updatedSelections);
        }
      } else if (newValue && isPresent && newValue.count > 0) {
        isBhc && addCert([newValue.cert, newValue.rssdhcr]);
        let matchingData: KeyValue = {};

        tableData.forEach((obj: KeyValue) => {
          if (isBhc && obj.RSSDHCR === newValue.rssdhcr) {
            matchingData = obj.subData.find(
              (data: KeyValue) => data.CERT === newValue.cert,
            );
          } else if (obj.CERT === newValue.cert) {
            matchingData = obj;
          }
        });
        if (matchingData?.count && matchingData.count === newValue.count) {
          addCert([newValue.cert]);
          const updatedSelections = partialMergerSelections.filter(
            (obj: KeyValue) => obj.cert !== newValue.cert,
          );
          updateUi("partialMerger", updatedSelections);
          setPartialMergerSelections(updatedSelections);
        } else {
          const values: KeyValue[] = partialMergerSelections.map((item) =>
            item.cert === newValue.cert
              ? { ...item, count: newValue.count, depsum: newValue.depsum }
              : item,
          );
          updateUi("partialMerger", values);
          setPartialMergerSelections(values);
        }
      } else if (newValue && newValue.count === 0) {
        const values: KeyValue[] = partialMergerSelections.filter(
          (item) => item.cert !== newValue.cert,
        );
        updateUi("partialMerger", values);
        setPartialMergerSelections(values);
        if (certsToMerge.length > 0 && certsToMerge.includes(newValue.cert)) {
          removeCert([newValue.cert]);
        }
      }
      setOpenModal("");

      if (isTestEnvironment) {
        appState.ui.closeModal = false;
        updateUi("closeModal", false);
      } else {
        updateUi("closeModal", false);
      }
    }
  }, [
    addCert,
    appState.ui,
    appState.ui.closeModal,
    appState.ui.partialMerger,
    cert,
    certsToMerge,
    partialMergerSelections,
    removeCert,
    tableData,
    updateUi,
    isBhc,
  ]);

  return (tableBody.length > 0 && !displayError) || loading ? (
    <Root>
      <Box
        sx={{
          marginTop: -1,
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            fontSize: 26,
            fontWeight: "bold",
            paddingBottom: "1rem",
          }}
        >
          {proFormaTitle}
        </Typography>
        <ApiDataDownload
          app="SOD"
          apiData={
            appState.ui.institutionType === "banks" ? [apiData] : apiDataAll
          }
        />
      </Box>
      <Divider />
      {searchPills}
      {appState.filters.stateNames[0] !== "" && <SelectedMarketTable />}
      {certAcquiring[0] && cert && branchOfficeModal}

      {!isBhc && (reportFinalized ? resetReportBanner : finalizeReportBanner)}
      {isBhc && (
        <>
          {orgTitle}
          <Grid container>
            <Grid
              size={{
                md: 12,
                lg: 7,
              }}
            >
              <MultiHeaderTable
                tableBody={
                  loading ? (
                    <LoadingIndicator text="Loading" spinnerSize={32} table />
                  ) : (
                    <>{totalOrgTable}</>
                  )
                }
                tableHeaders={bhcOrgTableHeaders}
              />
            </Grid>
            <Grid
              size={{
                md: 12,
                lg: 5,
              }}
            >
              <Paper
                elevation={3}
                style={{ height: "95%", boxShadow: "none" }}
                className={classes.finalizeReportPaper}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography
                    className={classes.bannerStyles}
                    variant="body1"
                    style={{
                      fontWeight: "bold",
                      paddingTop: "25px",
                      paddingLeft: "10px",
                      fontSize: "20px",
                      textAlign: "center",
                    }}
                  >
                    Make your proposed recombinations below, then click to
                    finalize the report.
                  </Typography>
                  {finalizeResetButton}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
      {importantInfoLink}
      {institutionsBanner()}
      <ResultPagination
        count={
          withBhc || appState.ui.institutionType === "banks"
            ? institutionsCount
            : noBHCData.length
        }
      >
        <MultiHeaderTable
          tableBody={
            loading ? (
              <LoadingIndicator text="Loading" spinnerSize={32} table />
            ) : (
              <>
                {tableBody}
                {recordCount > 0 && tableTotals}
                {hhiRows}
              </>
            )
          }
          tableHeaders={tableHeaders}
        />
      </ResultPagination>
      {isBhc && (
        <Typography className={classes.footerText}>
          *The total deposits for the Bank Holding Company is the combined Total
          Domestic Deposits of the underlying Insured Subsidiaries.
        </Typography>
      )}
      <br />
      {isBhc && footer}
    </Root>
  ) : (
    <Root>
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        No Results
      </Typography>
      <Divider />
      {searchPills}
      <Typography variant="body2" color="primary.main">
        Double-check your search criteria for errors, or try your search again
        limiting or removing location-based criteria.
      </Typography>
    </Root>
  );
}
